// Event Plan editor (redesign 260709, Phase 5) — HITL Gate 1 màn 3 tầng Scene → Event → Shot.
// Chạy 6 pass bằng nút, xem/sửa tay action/thoại/camera-window, badge continuity. Candidate/clip/camera-option = Epic 2.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useFilmPlanOptional } from './filmPlanContextBase';
import { isLayoutFourPlan, LAYOUT_FOUR_GEN_HINT } from './layoutFourGuard';
import { Card, Button, Tag, message, Space, Typography, Collapse, Select, Spin, Tooltip, Empty, Popconfirm, Drawer, Popover, Checkbox, Alert, Modal, InputNumber } from 'antd';
import { SkyEngineChips, SkyPhasesBlock } from './SkyVideoPanel'; // 261010 P1a
import { batchOverridesOf, legacyGenTuningOf, optInTuningOf } from './workflowGenerationUtils'; // 261010 UI2 (review #3): cấu hình ĐÃ LƯU của video opt-in
import { effectiveTuningLabel, genModelOptionsFor, isSkyPlan, overflowTagView } from './skyVideoMetaUtils'; // 261010 P1b: engine hiệu lực quyết model/nhãn ⚙️
import PositionMapPanel from './PositionMapPanel'; // 261010 P2-1: sơ đồ định vị (chỉ plan sky)
import { isPutLinksNoise } from './positionMapUtils';
import StoryboardBlock, { type StoryboardCtx } from './StoryboardBlock'; // 261010 P3-1: storyboard theo video (chỉ plan sky)
import { eventsSignature, finishedStoryboardJobs, STORYBOARD_JOB, storyboardJobName, storyboardsByEvent, storyboardErrorText } from './storyboardUtils';
import { LockOutlined, UnlockOutlined, DeleteOutlined, PlusOutlined, VideoCameraOutlined, RobotOutlined } from '@ant-design/icons';
import { filmApi } from '../../services/api/film';
import { apiErrorText, serverMessageOf } from './apiErrorText';
import { clipFailInfo, latestFailedClip } from './clipErrorText'; // 260813: nói LÝ DO clip hỏng ngay trên card
import { loadDismissedJobs, addDismissedJobs } from './dismissedJobsStore';
import { jobsFinishedSincePoll } from './jobPollUtils';
import type { EventPlan, EventPlanScene, FilmEvent, FilmShot, FilmDialogueMode, FilmBeatKind, BatchEditOp, FilmRunningJob, FilmFailedJob, GenTuning, FilmPromptMode, FilmDowngradePreview, Storyboard } from '../../types/film';
import PipelineStepper, { computeStatus, referencesIncomplete } from './PipelineStepper';
import {
  resolvePromptMode, promptModeStateOf, genGate, eventModeLabel, formatBlockedOrders, MODE_LABEL_VI,
  naturalLintHead, runRemainingBlockedMessages, shouldRebuildMismatchOf, rebuildCallOf,
  countRebuildableAfterModeSwitch, showRebuildButtonOf,
} from './promptModeUtils';
import FilmBankTab from './FilmBankTab';
import FilmExportTab from './FilmExportTab';
import FilmChatPanel, { ChatMsg } from './FilmChatPanel';
import PromptChipEditor from './PromptChipEditor';
import EnginePromptModal from './EnginePromptModal'; // 261002 batch 2 (A2)
import { applyStructToDraft, pruneOrphanFieldOps, structRejectReason, type StructOp } from './filmDraftStruct'; // E4-P2: op cấu trúc vào NHÁP
import { addShotHint, buildBeatSequence, isBeatMetaV2, validSplitPoints } from './beatSequenceUtils'; // 260923 E1-P1/P2/P3: event dạng chuỗi nhịp
import { resolveLocationByName } from '../../utils/filmLocationMatch'; // 260807: khớp scene.location ↔ Bank (tên hai nguồn khác nhau)
import { castNameMatches } from './characterCardUtils'; // 260922 E1-P2: khớp tên chính + tên phụ
import { downgradeErrorText, downgradeLossLines, downgradeRecoverySteps, downgradeTierLabel, type DowngradeRecoveryStep } from './skyDowngradeUtils';
import './storyboard.css';
import ShotCard from './EventShotCard'; // 261010 P2b-3: leaf trích ra file riêng (move-only) để khung mới dùng chung
import DialogueRow from './EventDialogueRow';

const STRUCT_TYPES = new Set(['splitEvent', 'mergeNext', 'insertAfter', 'deleteEvent']); // E4-P3: op cấu trúc bot đề xuất

// E6-P1: tên việc nền cho người đọc (khớp FILM_JOB ở backend services/filmJobs).
const JOB_LABEL: Record<string, string> = {
  FILM_AUTHOR_PROMPTS: 'Dựng prompt',
  FILM_GEN_EVENTS: 'Gen clip cả plan',
  FILM_GEN_CLIP: 'Gen clip',
  FILM_REGEN_CLIP: 'Gen lại clip',
  FILM_PREPARE_REFS: 'Chuẩn bị references',
  FILM_PREPARE_OUTFITS: 'Chuẩn bị trang phục',
  FILM_GEN_PLATES: 'Gen ảnh bối cảnh',
  FILM_ASSEMBLE: 'Dựng phim',
  // 260806: các pass kịch bản chuyển sang việc nền (gateway cắt request ở 180s — xem apiErrorText).
  FILM_GEN_PLAN: 'Tạo plan',
  FILM_DIALOGUE: 'Viết thoại',
  FILM_BREAKDOWN: 'Chốt location',
  FILM_DIRECT: 'Dựng camera',
  FILM_CONTINUITY: 'Soát continuity',
  FILM_DURATIONS: 'Tính thời lượng',
  FILM_SCRIPT_PASSES: 'Các bước kịch bản',
  // 261010 P2-1: sơ đồ định vị (plan sky).
  FILM_POSITION_MAP: 'Gen ảnh sơ đồ',
  FILM_POSITION_SETUPS: 'Đề xuất setup sơ đồ',
  // 261010 P3-1: storyboard (plan sky) — soạn khung và gen tấm dùng chung type, phân biệt bằng `phase` (xem jobTitle).
  FILM_STORYBOARD: 'Storyboard (luồng mới)',
};
/** Tên việc nền hiện ở băng cấp plan; FILM_STORYBOARD thêm tên theo phase (Soạn khung / Gen tấm). */
const jobTitle = (type: string, phase?: string): string => (type === STORYBOARD_JOB ? `Storyboard · ${storyboardJobName(phase)}` : (JOB_LABEL[type] || type));
const { Text, Paragraph } = Typography;

/**
 * Thời điểm trong ngày của CẢNH (260811) — thứ quyết ánh sáng của mọi clip trong cảnh đó.
 *
 * AI suy từ heading lúc dựng plan; ô này để admin đè khi nó đọc sai. Vì sao cần: `[Time]` trong prompt
 * trước đây lấy từ ledger (chuỗi tự do do AI suy lại mỗi scene) nên trôi — đo prod 10/08 có 16 giá trị
 * khác nhau (`unknown`, `16:58`…) và 10.8% event nói ngược heading của chính cảnh mình; có cảnh vườn ĐÊM
 * mà `[Time]` nhảy Night/day giữa các event, clip ra ban ngày.
 */
const TIME_OPTIONS = [
  { value: 'DAWN', label: '🌅 Dawn' },
  { value: 'MORNING', label: '🌤 Morning' },
  { value: 'DAY', label: '☀️ Day' },
  { value: 'AFTERNOON', label: '🌇 Afternoon' },
  { value: 'DUSK', label: '🌆 Dusk' },
  { value: 'EVENING', label: '🌃 Evening' },
  { value: 'NIGHT', label: '🌙 Night' },
];
const TIME_HINT = 'Time of day for this scene — drives the lighting of every clip in it. Filled in from the heading; override here. Clear it to fall back to the heading. Re-author prompts after changing.';
const TIME_LABEL: Record<string, string> = Object.fromEntries(TIME_OPTIONS.map((o) => [o.value, o.label]));

/**
 * Giờ trong ngày của cảnh — **im lặng khi heading đã nói rồi**.
 *
 * 🔴 Bản đầu để hẳn một Select luôn hiện cạnh mỗi heading: user nhìn `S2. EXT. NIGHT ROAD - NIGHT` kèm một ô
 * "time of day" TRỐNG và thấy kì — đúng, vì heading đã nói NIGHT nên ô trống trông như dữ liệu bị thiếu,
 * trong khi thực ra chẳng thiếu gì. Điều khiển chỉ nên xuất hiện khi có việc cho người dùng làm.
 *
 * Ba trạng thái:
 *  - `source: 'heading'` (thường gặp) → chỉ một nút ⏱ mờ, bấm mới mở. Không chiếm chỗ, không đòi hỏi gì.
 *  - `source: 'scene'`  → chip nổi: người đã đè lên heading, phải nhìn thấy được là đang khác.
 *  - không nơi nào nói được → chip CAM cảnh báo: đây là ca prompt sẽ thiếu `[Time]` và engine tự bịa ánh sáng.
 */
const SceneTime: React.FC<{
  scene: EventPlanScene;
  onChange: (sceneId: string, timeOfDay: string | null) => void;
}> = ({ scene, onChange }) => {
  const [open, setOpen] = useState(false);
  const resolved = scene.timeOfDayResolved;
  const overridden = resolved?.source === 'scene';
  const missing = !resolved?.value;
  const label = resolved?.value ? (TIME_LABEL[resolved.value] || resolved.value) : null;

  const editor = (
    <Select
      size="small" style={{ width: 148 }} allowClear autoFocus defaultOpen
      placeholder="from heading" value={scene.timeOfDay || undefined} options={TIME_OPTIONS}
      onChange={(v) => { onChange(scene.id, v ?? null); setOpen(false); }}
    />
  );

  return (
    <Popover
      open={open} onOpenChange={setOpen} trigger="click" placement="bottom"
      content={<div onClick={(e) => e.stopPropagation()}>{editor}</div>}
      title={<span style={{ fontSize: 12, fontWeight: 400 }}>{TIME_HINT}</span>}
    >
      <span onClick={(e) => e.stopPropagation()}>
        {overridden || missing ? (
          <Tag color={missing ? 'orange' : 'blue'} style={{ cursor: 'pointer', marginInlineEnd: 0 }}>
            {missing ? '⏱ no time of day' : `${label} · overridden`}
          </Tag>
        ) : (
          <Tooltip title={`${label} — from the heading. Click to override.`}>
            <Button type="text" size="small" style={{ opacity: 0.45 }}>⏱</Button>
          </Tooltip>
        )}
      </span>
    </Popover>
  );
};

// Storyboard: readable shot-size labels (hide the raw codes).
const SIZE_LABEL: Record<string, string> = {
  EWS: 'Extreme wide', WS: 'Wide', MWS: 'Medium wide', MS: 'Medium',
  MCU: 'Medium close-up', CU: 'Close-up', ECU: 'Extreme close-up',
};
const sizeVN = (s?: string | null) => SIZE_LABEL[String(s || '').toUpperCase()] || (s ? String(s) : 'Shot');
const shotDesc = (sh: FilmShot) => sh.frameNote || (sh as any).purpose || sh.action || '(no description)';
// màu avatar chữ-cái theo tên (ổn định) khi nhân vật chưa có ảnh mặt
const avaHue = (s: string) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360; return h; };
const Face: React.FC<{ name: string; url?: string | null }> = ({ name, url }) => (
  <Tooltip title={name}>
    {url ? <img className="sb-ava" src={url} alt={name} />
      : <span className="sb-ava" style={{ background: `hsl(${avaHue(name)} 42% 50%)` }}>{name.slice(0, 1).toUpperCase()}</span>}
  </Tooltip>
);

const PASSES: { key: string; label: string; run: (planId: string) => Promise<any> }[] = [
  { key: 'breakdown', label: '📍 Breakdown location', run: (p) => filmApi.breakdownLocations(p) },
  { key: 'dialogue', label: '💬 Dialogue', run: (p) => filmApi.authorDialogue(p) },
  { key: 'direct', label: '🎥 Directing', run: (p) => filmApi.directPlan(p) },
  { key: 'durations', label: '⏱️ Duration', run: (p) => filmApi.computeDurations(p) },
  { key: 'continuity', label: '🔗 Continuity', run: (p) => filmApi.checkContinuity(p) },
  // Epic3 260722 — dạng II (NARRATION): giọng người kể + V.O. lấp event câm (chạy SAU durations).
  { key: 'voices', label: '🔊 Giọng kể (V.O.)', run: (p) => filmApi.ensureNarrationVoices(p) },
  { key: 'voFill', label: '🎙 V.O. fill (dạng II)', run: (p) => filmApi.fillNarration(p) },
];

/** Sửa nhân vật có mặt (event/scene): bấm cụm mặt → chọn lại danh sách. Ghi vào `presence.visible` của các shot. */
const CastEditor: React.FC<{
  names: string[]; options: string[]; onChange: (names: string[]) => void; children: React.ReactNode;
}> = ({ names, options, onChange, children }) => (
  <Popover
    trigger="click" placement="bottomRight"
    content={<div onClick={(e) => e.stopPropagation()}>
      <Select mode="multiple" size="small" style={{ minWidth: 240 }} placeholder="Chọn nhân vật" value={names}
        options={options.map((n) => ({ label: n, value: n }))} onChange={onChange} />
    </div>}
    title={<span style={{ fontSize: 12, fontWeight: 400 }}>Nhân vật xuất hiện</span>}
  >
    <span style={{ cursor: 'pointer' }} title="Bấm để sửa nhân vật" onClick={(e) => e.stopPropagation()}>{children}</span>
  </Popover>
);

/** Sửa thời lượng event (giây) — ghi genDurationS vào nháp. */
const DurationEditor: React.FC<{ value: number | null | undefined; onChange: (v: number) => void }> = ({ value, onChange }) => (
  <Popover
    trigger="click" placement="bottom"
    content={<div onClick={(e) => e.stopPropagation()}>
      <InputNumber size="small" min={1} max={15} step={1} value={value ?? undefined} addonAfter="s"
        onChange={(v) => { if (typeof v === 'number' && v > 0) onChange(v); }} />
    </div>}
  >
    <span className="sb-tag" style={{ cursor: 'pointer' }} title="Bấm để sửa thời lượng" onClick={(e) => e.stopPropagation()}>⏱ {value ?? '?'}s ✎</span>
  </Popover>
);

const CLIP_STATUS_COLOR: Record<string, string> = { PENDING: 'processing', DONE: 'success', FAILED: 'error' };

/**
 * Nội dung tooltip cho một clip hỏng: khâu hỏng + có tốn credit không → NGUYÊN VĂN lỗi Seedance.
 * Cố tình nói luôn "chưa tốn credit" ở ca đầu-vào: đó là thứ quyết định người vận hành có dám bấm lại hay không.
 * 🔴 Không diễn giải từng mã lỗi (Seedance đẻ mã mới liên tục, đoán = đoán sai) — hiện thẳng để tự đọc.
 */
const FailTip: React.FC<{ info: NonNullable<ReturnType<typeof clipFailInfo>>; raw?: string | null }> = ({ info, raw }) => (
  <div style={{ fontSize: 12, lineHeight: 1.5 }}>
    <div style={{ fontWeight: 600, marginBottom: 3 }}>
      {info.label}
      {info.billed
        ? <span style={{ opacity: .75, fontWeight: 400 }}> · đã tính tiền lượt gen này</span>
        : <span style={{ opacity: .75, fontWeight: 400 }}> · chưa tốn credit</span>}
    </div>
    <div style={{ marginBottom: 5 }}>{info.hint}</div>
    <div style={{ opacity: .7, marginBottom: 2 }}>Nguyên văn từ Seedance:</div>
    <div style={{ maxHeight: 150, overflow: 'auto', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 10.5, whiteSpace: 'pre-wrap', background: 'rgba(0,0,0,.25)', padding: '4px 6px', borderRadius: 3 }}>
      {raw || info.code || '(không có chi tiết)'}
    </div>
  </div>
);

const EventCard: React.FC<{
  ev: FilmEvent; onSaveEvent: (id: string, d: any) => void; onSaveDialogue: (id: string, lines: any[]) => void;
  onSaveShot: (id: string, d: Record<string, unknown>) => void; onDeleteShot: (id: string) => void;
  onSetEventCast: (eventId: string, names: string[]) => void;
  onMoveShot: (ev: FilmEvent, id: string, dir: -1 | 1) => void; onAddShot: (eventId: string) => void;
  /** 261030 C1 — plan layout four: ẨN mọi nút gen (gen video ở bước 4 của khung 4 bước). */
  layoutFour?: boolean;
  onGen: (eventId: string, variant?: boolean) => void; onSelectClip: (eventId: string, clipId: string) => void; busy?: boolean;
  onSavePrompt: (eventId: string, prompt: string) => void; refBank?: import('../../types/film').EventRefBank; sceneLocation?: string | null;
  // 260927 mục J — engine hiện tại (`plan.engine`), truyền xuống PromptChipEditor để chip nhân vật đọc đúng cờ sẵn sàng.
  engine?: 'AVIS' | 'BYTEPLUS';
  onDeleteClip: (clipId: string) => void;
  // Epic 5 — CRUD event (sửa ranh giới AI chia)
  onSplit: (eventId: string, opt: { keepShotCount?: number; keepBeatCount?: number }) => void; onMergeNext: (eventId: string) => void; // E1-P2: v2 tách theo nhịp
  onInsertAfter: (eventId: string) => void; onDeleteEvent: (eventId: string) => void; canMergeNext: boolean;
  // 260715 Epic 1 P1 — highlight nháp chưa lưu
  dirty?: boolean; dirtyShotIds?: string[];
  // E6-P1 — event này đang có job gen ở nền (nguồn: server)
  generating?: boolean;
  // E6-P2 — tuỳ chọn gen, đặt cạnh nút gen của từng event (không phải thanh cấp plan)
  tuning: GenTuning; onTuningChange: (t: GenTuning) => void;
  // 261010 P1b — đổi engine của event sky; component chỉ lo Popconfirm, parent gọi API/reload.
  onChangeEngine?: (eventId: string, engine: import('../../types/film').FilmVideoEngine, acceptEditedPrompt?: boolean) => Promise<'changed' | 'promptEdited' | 'needsSplit' | 'failed'>;
  onOpenDowngrade?: (eventId: string) => void;
  downgradeRecovery?: { steps: DowngradeRecoveryStep[]; busy?: boolean; onRun: (key: DowngradeRecoveryStep['key']) => void };
  // 260923 Epic2-P1 Bước 8 — mode prompt cấp plan (đọc mode hiệu lực) + đổi mode ĐÈ của riêng event này.
  planPromptMode: FilmPromptMode; onSetEventPromptMode: (eventId: string, mode: FilmPromptMode | null) => void;
  // 260923 Epic2-P3 (M3) — nút thẻ "Dựng lại prompt" (MISMATCH hoặc MISSING+canAuthor); `force` chỉ khi EDITED (đã hỏi).
  onRebuildEventPrompt?: (eventId: string, force?: boolean) => void;
  rebuildBusy?: boolean;
  // 261010 P1a — plan 'sky' ⇒ chip engine + khối Giai đoạn (chỉ đọc); vắng/null ⇒ không hiện gì mới.
  flowVersion?: import('../../types/film').FilmFlowVersion | null;
  // 261010 P3-1 — dữ liệu storyboard cấp plan (chỉ truyền khi plan sky); thiếu ⇒ không render khối Storyboard.
  storyboard?: StoryboardCtx;
}> = ({ ev, onSaveEvent, onSaveDialogue, onSaveShot, onDeleteShot, onSetEventCast, onMoveShot, onAddShot, layoutFour, onGen, onSelectClip, busy, onSavePrompt, refBank, sceneLocation, engine, onDeleteClip, onSplit, onMergeNext, onInsertAfter, onDeleteEvent, canMergeNext, dirty, dirtyShotIds, generating, tuning, onTuningChange, onChangeEngine, onOpenDowngrade, downgradeRecovery, planPromptMode, onSetEventPromptMode, onRebuildEventPrompt, rebuildBusy, flowVersion, storyboard }) => {
  const bm: any = ev.beatMeta || {};
  const issues: string[] = (ev.continuityLedger?.warnings as any) || [];
  /**
   * Cảnh báo lúc dựng prompt (260811): lệch giờ heading↔ledger · ánh sáng bible nghịch thời điểm ·
   * nhãn V.O. thiếu mệnh đề giới. Tách khỏi `issues` (continuity) vì hai loại này dẫn tới hai hành động
   * khác nhau: continuity thì sửa action/ledger, còn mấy cái đây thì sửa giờ của cảnh hoặc giới trong Bank.
   */
  const promptWarns: string[] = (ev.promptWarnings as any) || [];
  const cast = (refBank?.characters || []).map((c) => c.name);
  const castOpts = cast.map((n) => ({ label: n, value: n }));
  /**
   * câu thoại #N ↔ shot đang giữ nó (`presence.speaksDialogueIndex`, 1-based theo event).
   * Dùng hai chiều: ShotCard khoá option đã có chủ, DialogueRow báo câu nào còn mồ côi.
   */
  const lineTaken = new Map<number, { shotId: string; order: number }>();
  for (const s of ev.shots || []) {
    const i = (s.presence as any)?.speaksDialogueIndex;
    if (typeof i === 'number' && i > 0 && !lineTaken.has(i)) lineTaken.set(i, { shotId: s.id, order: s.orderIndex });
  }
  const rebuild = (fn: (base: any[]) => any[]) => {
    // 260923: mang NEO NHỊP theo từng câu — sửa chữ/người nói/mode không được làm rơi neo (dòng mới = null).
    const base = (ev.dialogueLines || []).map((d) => ({ speakerName: d.speakerName, textEn: d.textEn, dialogueMode: d.dialogueMode, afterActionBeatOrder: d.afterActionBeatOrder ?? null }));
    onSaveDialogue(ev.id, fn(base));
  };
  const setLine = (idx: number, patch: any) => rebuild((base) => base.map((d, i) => i === idx ? { ...d, ...patch } : d));
  const deleteLine = (idx: number) => rebuild((base) => base.filter((_, i) => i !== idx));
  const addLine = () => rebuild((base) => [...base, { speakerName: '', textEn: '', dialogueMode: 'ON_CAMERA', afterActionBeatOrder: null }]);
  const shotCount = (ev.shots || []).length;

  // ── Storyboard (260715): panel ẢNH TO 9:16 + caption + danh sách shot đọc-được; editor kỹ thuật cũ gói vào "Chỉnh sâu". ──
  const simple = false; // Card "Chỉnh sâu" tái dùng nguyên editor cũ (mọi ref `simple` → false = hiện đầy đủ)
  const selected = (ev.clips || []).find((c) => c.id === ev.selectedClipId) || (ev.clips || []).find((c) => c.status === 'DONE');
  const pending = (ev.clips || []).some((c) => c.status === 'PENDING');
  const fromShots = Array.from(new Set((ev.shots || []).flatMap((s) => s.presence?.visible || [])));
  const speakers = Array.from(new Set((ev.dialogueLines || []).map((d) => d.speakerName).filter(Boolean) as string[]));
  const who = fromShots.length ? fromShots : speakers;   // ai hiện diện: ưu tiên shot presence, fallback người nói
  const faceOf = (n: string) => (refBank?.characters || []).find((c) => castNameMatches(c, n)); // 260922 E1-P2: cả tên phụ
  // 260807: khớp RỘNG — Bank đặt tên theo heading ("MORTAIN MANSION"), scene.location do AI ghi tự do
  // ("Mortain Mansion - Party Room"), so === thì không bao giờ ra và ô ảnh bối cảnh trống trơn.
  const plateUrl = resolveLocationByName(sceneLocation, refBank?.locations)?.plateUrl || null;
  const isPeak = String(bm.dramaticWeight || '').toUpperCase() === 'PEAK';
  const isNewDraft = ev.id.startsWith('tmp-'); // E4-P2: event do split/insert sinh trong nháp
  // 260923 E1-P1: event dạng CHUỖI NHỊP — hiện nhịp đánh số xen câu thoại theo neo; plan cũ (v1) không đổi gì.
  const isV2 = isBeatMetaV2(ev.beatMeta);
  const beatSeq = isV2 ? buildBeatSequence(bm.actionBeats, ev.dialogueLines) : [];
  // E1-P2 (R5, R8): ✂️ của event dạng chuỗi chỉ liệt kê ranh giới hợp lệ — tính trên NHÁP (sau gộp/tách trong nháp vẫn đúng).
  const beatSplitPoints: number[] = isV2 ? validSplitPoints(bm.actionBeats || [], bm.sourceLines || []) : [];
  const dur = ev.genDurationS ?? selected?.durationS ?? null;
  // Epic 2: staleness — nội dung đổi sau khi author/gen.
  const stale = ev.staleStages || [];
  const promptStale = stale.includes('prompt');
  const clipStale = stale.includes('clip');
  const continuityStale = stale.includes('continuity');
  // E6-P1: event này đang gen ở nền (job server, hoặc clip PENDING còn trong DB) → KHOÁ nút.
  // Trước đây nút vẫn bấm được trong lúc clip đang PENDING: mỗi cú bấm thêm một clip nữa, tính tiền lần nữa.
  // (Khoá thật nằm ở server và trả 409; đây chỉ là để người dùng khỏi phải phát hiện điều đó bằng cách bấm.)
  const clipPending = (ev.clips || []).some((c) => c.status === 'PENDING');
  const genLocked = generating || clipPending;
  /**
   * 260813: LÝ DO của lượt gen hỏng gần nhất. Trước đây clip FAILED không rơi vào nhánh nào của khung 9:16
   * (chỉ xét `videoUrl` và `pending`) nên card hiện "not generated" y như chưa từng bấm — đo trên prod có
   * 144 clip hỏng câm lặng kiểu này.
   */
  const failedClip = latestFailedClip(ev.clips);
  const lastFail = clipFailInfo(failedClip);
  /**
   * Khi event ĐÃ có bản dùng được thì chỉ báo nếu cú bấm hỏng xảy ra SAU bản đó (ca E14 prod: DONE rồi v7/v8 hỏng).
   * Ngược lại — hỏng trước, sau đó gen lại thành công — coi như chuyện đã xử lý xong, dán nhãn đỏ lên là báo động giả.
   */
  const failIsNewer = !!failedClip && (!selected || String(failedClip.createdAt || '') > String(selected.createdAt || ''));
  /**
   * 260814: cảnh báo của LƯỢT GEN GẦN NHẤT — chip đạo cụ không đính được ảnh, tham chiếu rớt…
   * Server ghi vào `meta.refWarnings` thay vì chỉ `logger.warn`, vì prod chạy `LOG_LEVEL=error` nên log warn
   * bốc hơi: người vận hành bấm chèn chip, gen ra clip không có đồ vật, và không có gì nói cho họ biết vì sao.
   */
  const lastClip = (ev.clips || []).reduce<any>((a, b) => (!a || String(b.createdAt || '') > String(a.createdAt || '') ? b : a), null);
  const refWarnings: string[] = ((lastClip?.meta as any)?.refWarnings as string[]) || [];

  // E6-P2 (sửa sau góp ý): tuỳ chọn gen nằm NGAY CẠNH nút gen của từng event, không phải ở thanh cấp plan.
  // Lý do: mỗi cảnh cần khác nhau — cảnh hành động đáng dùng pro, cảnh tĩnh để mini là đủ. Đặt ở cấp plan
  // thì người vận hành phải nhớ "mình đang để chế độ gì" trước mỗi cú bấm, và nhớ nhầm là mất tiền.
  const savedTuning = optInTuningOf(ev); // video của plan opt-in: model / độ phân giải là cấu hình ĐÃ LƯU (đổi ở khung mới); ⚙️ chỉ còn chọn số bản
  const costly = (isSkyPlan(flowVersion) && ev.videoEngine === 'SEEDANCE_25') || (tuning.count || 1) > 1
    || (savedTuning ? (savedTuning.model === 'pro' || savedTuning.resolution !== '480p')
      : (tuning.model === 'pro' || tuning.model === '2.5' || tuning.resolution === '1080p'));
  const selectedModel = savedTuning ? (savedTuning.model ?? '2.5') : isSkyPlan(flowVersion) && ev.videoEngine === 'SEEDANCE_25' ? '2.5' : (tuning.model || 'mini');
  const tuningPanel = (
    <div style={{ width: 232 }}>
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Model</div>
        <Select size="small" style={{ width: '100%' }} value={selectedModel} disabled={!!savedTuning}
          onChange={(v) => onTuningChange({ ...tuning, model: v })}
          options={genModelOptionsFor(flowVersion, ev.videoEngine)} />
      </div>
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Độ phân giải</div>
        <Select size="small" style={{ width: '100%' }} value={savedTuning ? savedTuning.resolution : (tuning.resolution || '480p')} disabled={!!savedTuning}
          onChange={(v) => onTuningChange({ ...tuning, resolution: v })}
          options={[{ label: '480p', value: '480p' }, { label: '720p', value: '720p' }, { label: '1080p', value: '1080p' }]} />
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Số bản gen ra</div>
        <Select size="small" style={{ width: '100%' }} value={tuning.count || 1}
          onChange={(v) => onTuningChange({ ...tuning, count: v })}
          options={[{ label: '1 bản', value: 1 }, { label: '2 bản để so', value: 2 }, { label: '3 bản để so', value: 3 }]} />
      </div>
      {savedTuning ? <div style={{ fontSize: 10.5, color: '#999', marginBottom: 6 }} data-testid="legacy-saved-gen">Model và độ phân giải đã lưu cho video này — đổi ở bước Video của khung mới (ô Engine).</div> : null}
      <div style={{ fontSize: 10.5, color: '#999', marginTop: 8, lineHeight: 1.45 }}>
        Chỉ áp cho clip này, và quên khi bạn đóng tab. ⚠️ Credit tính theo <b>từng model</b> — chọn model chưa mua gói
        sẽ lỗi ngay lúc gửi. Nhiều bản chạy tuần tự, bản đầu lỗi thì dừng.
      </div>
    </div>
  );
  /** Nút mở tuỳ chọn — nhãn hiện luôn lựa chọn đang dùng, khỏi phải mở ra mới biết sắp tiêu bao nhiêu. */
  const tuningBtn = (block?: boolean) => (
    <Popover trigger="click" placement="bottomLeft" title="Gen clip NÀY bằng — chỉ áp cho clip này" content={tuningPanel}>
      <Button size="small" block={block} style={block ? { marginTop: 4 } : undefined}
        danger={costly} type={costly ? 'dashed' : 'default'}>
        ⚙️ {effectiveTuningLabel(tuning, flowVersion, ev.videoEngine, ev.generation)}
      </Button>
    </Popover>
  );

  const [engineViewId, setEngineViewId] = useState<string | null>(null); // 261002 batch 2 (A2): "Xem bản gửi engine"
  // 260923 Epic2-P1 Bước 8 — mode hiệu lực + trạng thái so snapshot, tính THUẦN (mirror BE, không tin field
  // BE gửi kèm vì `ev` ở đây là bản NHÁP có thể vừa bị sửa tay chưa qua round-trip server).
  const effectivePromptMode = resolvePromptMode(ev.promptMode, planPromptMode);
  const promptModeState = promptModeStateOf({ effectiveMode: effectivePromptMode, authorPrompt: ev.authorPrompt, authorPromptMode: ev.authorPromptMode });
  const modeGate = genGate({ effectiveMode: effectivePromptMode, authorPrompt: ev.authorPrompt, authorPromptMode: ev.authorPromptMode });
  const modeLabel = eventModeLabel({ promptMode: ev.promptMode, authorPromptMode: ev.authorPromptMode, authorStatus: ev.authorStatus, effectiveMode: effectivePromptMode, state: promptModeState });

  // Gen khi prompt cũ → cảnh báo (không chặn cứng). Cửa mode (Bước 6, U2) đứng TRƯỚC mọi nhánh khác — tắt
  // cứng, không cho "gen dù biết" như đường promptStale (đằng này server sẽ 409, không phải chỉ chất lượng thấp).
  // `inFrame` (F2): CHỈ nút Generate chính trong khung 9:16 nền tối cần class riêng để chữ đọc được khi
  // disabled — nút "↻ Regenerate"/ADVANCED nằm trên nền sáng, disabled mặc định của antd đã đọc được.
  const genBtn = (variant: boolean, node: React.ReactElement<any>, inFrame = false) =>
    layoutFour ? null
      : !modeGate.ok
      ? <Tooltip title={modeGate.message}>{React.cloneElement(node, { onClick: undefined, disabled: true, className: inFrame ? 'sb-modegate-btn' : undefined })}</Tooltip>
      : genLocked
      ? <Tooltip title={`Event này đang gen ở nền${clipPending ? ' (có clip đang chờ)' : ''} — chờ xong rồi hãy tạo bản mới, bấm thêm là tính tiền thêm`}>
          {React.cloneElement(node, { onClick: undefined, disabled: true })}
        </Tooltip>
      : isNewDraft
      // E4-P2: event mới chỉ tồn tại trong nháp → chưa gen được, phải Lưu trước.
      ? <Tooltip title="Event mới chưa lưu — bấm 💾 Lưu trước khi tạo video">{React.cloneElement(node, { onClick: undefined, disabled: true })}</Tooltip>
      : promptStale
      ? <Popconfirm title="Prompt is out of date" description="Sync prompts first for an accurate clip, or generate with the old prompt." okText="Generate anyway" cancelText="Cancel" onConfirm={() => onGen(ev.id, variant)}>{React.cloneElement(node, { onClick: undefined })}</Popconfirm>
      : React.cloneElement(node, { onClick: () => onGen(ev.id, variant) });

  return (
    <div className={`sb-panel${dirty ? ' dirty' : ''}`} data-event-id={ev.id}>
      {/* Khung phim 9:16 — clip đã chọn ▶ / đang tạo ⏳ / chưa có → Tạo video */}
      <div>
        <div className="sb-frame">
          {selected?.videoUrl
            ? <video src={selected.videoUrl} controls />
            : pending
              ? <><div className="sb-shimmer" /><div className="sb-gen"><div className="sb-ring" /><span className="sb-genlbl">Generating…</span></div></>
              : <>
                  {plateUrl && <img className="dim" src={plateUrl} alt="" />}
                  <div className="sb-vig" />
                  <div className="sb-fc">
                    {genBtn(false, <Button type="primary" size="small" icon={<VideoCameraOutlined />} loading={busy}>Generate</Button>, true)}
                    {/* 260813: chưa có clip nào chạy được — nói NGAY lý do lần gen gần nhất, đừng để "not generated" nói dối.
                        260923 Epic2-P1 F2: lastFail vẫn thắng như cũ (clip hỏng thì báo lỗi gen trước); kế đó mới tới lý do
                        cửa mode (cùng chữ với tag ở sb-head) — "not generated" chỉ còn đúng khi cả hai đều không có gì báo. */}
                    {lastFail
                      ? <Tooltip title={<FailTip info={lastFail} raw={failedClip?.error} />} overlayStyle={{ maxWidth: 420 }}>
                          <span className="sb-failhint">✕ {lastFail.label}<i>vì sao?</i></span>
                        </Tooltip>
                      : !modeGate.ok
                      ? <Tooltip title={modeGate.message}><span className="sb-hint">{modeLabel.head?.text}</span></Tooltip>
                      : <span className="sb-hint">not generated</span>}
                  </div>
                </>}
          {selected?.videoUrl && dur != null && <div className="sb-durbadge">{dur}s</div>}
          {selected?.videoUrl && clipStale && <div className="sb-clipstale">⚠ out of date</div>}
          {/* Đã có bản dùng được nhưng cú bấm SAU đó hỏng (ca E14 prod: DONE rồi v7/v8 hỏng) → vẫn phải báo. */}
          {selected?.videoUrl && lastFail && failIsNewer && (
            <Tooltip title={<FailTip info={lastFail} raw={failedClip?.error} />} overlayStyle={{ maxWidth: 420 }}>
              <div className="sb-failbadge">✕ bản mới hỏng</div>
            </Tooltip>
          )}
          {!selected?.videoUrl && !pending && shotCount > 0 && <div className="sb-shotbadge"><b>{shotCount} shots</b></div>}
        </div>
        {selected?.videoUrl && genBtn(true, <Button size="small" block style={{ marginTop: 6 }} loading={busy}>↻ Regenerate</Button>)}
        {tuningBtn(true)}
      </div>

      {/* Nội dung */}
      <div style={{ minWidth: 0 }}>
        <div className="sb-head">
          <span className="sb-slate">E{ev.eventOrder}</span>
          {isNewDraft && <Tooltip title="Event mới trong nháp — chưa tồn tại trên server, bấm 💾 Lưu để tạo"><span className="sb-tag warn">🆕 mới</span></Tooltip>}
          {isPeak && <span className="sb-tag peak">🔥 PEAK</span>}
          <DurationEditor value={ev.genDurationS} onChange={(v) => onSaveEvent(ev.id, { genDurationS: v })} />
          {/* 261010 P1b: engine có hiệu lực cho lượt gen; chỉ plan sky hiện chip/nút đổi. */}
          <SkyEngineChips ev={ev} flowVersion={flowVersion} onChangeEngine={onChangeEngine} onOpenDowngrade={onOpenDowngrade} />
          {/* 260923 Epic2-P1 F2 — lý do "vì sao nút gen tắt" phải THẤY ĐƯỢC ở đầu thẻ, không chỉ trong
              ADVANCED (blackbox bước 6, ảnh e2-s6-after.png). Tag khu ADVANCED (dưới, Candidate clip) GIỮ NGUYÊN. */}
          {modeLabel.head && (
            <Tooltip title={modeGate.ok ? undefined : modeGate.message}>
              <span className={`sb-tag ${modeLabel.head.cls}`}>{modeLabel.head.text}</span>
            </Tooltip>
          )}
          {/* 260923 Epic2-P3 (M3) — nút "Dựng lại prompt": MISMATCH, hoặc MISSING mà canAuthor=true (đường gỡ
              duy nhất cho prompt sửa tay lệch mode — U1 để chúng giữ mode cũ, chỉ gỡ qua nút này).
              260925 (UT4) — thêm: prompt sai khuôn Tự nhiên (`naturalPromptLint`, kể cả EDITED) — điều kiện
              gom vào `showRebuildButtonOf` (promptModeUtils.ts). */}
          {/* 260927 mục N5 — "lệch bộ đồ": chip look: trong prompt ≠ bảng gán cảnh hiện tại (đổi bộ/xoá dòng/AI
              gán lại SAU khi đã author). Cảnh báo, không chặn gen — chỉ hiện chip + mở đường "Dựng lại prompt". */}
          {ev.promptLookMismatch?.mismatch && (
            <Tooltip title={`Prompt đang ghim bộ đồ CŨ cho: ${ev.promptLookMismatch.characterNames.join(', ')} — bảng gán cảnh đã đổi. Bấm "Dựng lại prompt" để cập nhật.`}>
              <span className="sb-tag warn">⚠ lệch bộ đồ</span>
            </Tooltip>
          )}
          {onRebuildEventPrompt && showRebuildButtonOf({
            promptModeState, effectiveMode: effectivePromptMode, beatMeta: ev.beatMeta, shotCount,
            spokenLineCount: (ev.dialogueLines || []).filter((d) => d.textEn && String(d.dialogueMode || '').toUpperCase() !== 'NONE').length,
            naturalPromptLint: ev.naturalPromptLint,
            lookMismatch: ev.promptLookMismatch?.mismatch,
          }) && (() => {
            const call = rebuildCallOf(ev);
            const title = ev.promptLookMismatch?.mismatch
              ? 'Ghi đè prompt sửa tay để cập nhật bộ đồ theo bảng gán cảnh hiện tại? (1 lượt AI)'
              : `Ghi đè prompt sửa tay bằng bản AI dựng theo mode ${MODE_LABEL_VI[effectivePromptMode]}? (1 lượt AI)`;
            return call.needsConfirm
              ? (
                <Popconfirm
                  title={title}
                  okText="Ghi đè" cancelText="Huỷ"
                  onConfirm={() => onRebuildEventPrompt(ev.id, call.force)}
                >
                  <Button size="small" loading={rebuildBusy}>Dựng lại prompt</Button>
                </Popconfirm>
              )
              : <Button size="small" loading={rebuildBusy} onClick={() => onRebuildEventPrompt(ev.id, call.force)}>Dựng lại prompt</Button>;
          })()}
          {modeLabel.showModeInHead && (
            <Tooltip title="Mode prompt hiệu lực của event này — đè riêng, không theo plan">
              <span className="sb-tag">{modeLabel.modeText}{modeLabel.suffix}</span>
            </Tooltip>
          )}
          {/* 260923 Epic2-P2 (N8, Bước 7) — cảnh báo "prompt sai khuôn Tự nhiên" cạnh tag mode ở F2, tính
              lúc đọc (bắt cả prompt do tay/chatbot/code cũ ghi, không chỉ prompt vừa AI dựng). */}
          {naturalLintHead(ev.naturalPromptLint) && (
            <Tooltip title={(ev.naturalPromptLint || []).join(' · ')}>
              <span className={`sb-tag ${naturalLintHead(ev.naturalPromptLint)!.cls}`}>{naturalLintHead(ev.naturalPromptLint)!.text}</span>
            </Tooltip>
          )}
          {/* E1-P2 (Bước 9): giây ước THEO NHỊP vượt trần 15s — BE tính lúc đọc (`beatTiming`), event v1 không có. */}
          {isV2 && (() => {
            const ov = overflowTagView(ev.beatTiming, flowVersion, ev.videoEngine); // 261010 S3/S5: sky + 2.5 luôn hiện chữ không số, sky + 2.0 ẩn, ca khác y cũ
            return ov && (
              <Tooltip title={ov.tip}>
                <Tag color="orange" style={{ margin: 0 }}>{ov.text}</Tag>
              </Tooltip>
            );
          })()}
          {selected?.videoUrl && !clipStale && <span className="sb-tag done">✓ Done</span>}
          {stale.length > 0 && <Tooltip title={`Out of date after an edit — ${[continuityStale && 'continuity', promptStale && 'prompt', clipStale && 'clip'].filter(Boolean).join(' + ')} need re-sync`}><span className="sb-tag stale">⚠ out of date</span></Tooltip>}
          {dirty && <span className="sb-tag warn">● Unsaved</span>}
          <CastEditor names={who} options={cast} onChange={(v) => onSetEventCast(ev.id, v)}>
            <div className="sb-facemini">{who.length ? who.map((n) => <Face key={n} name={n} url={faceOf(n)?.previewUrl} />) : <span className="sb-hint">+ nhân vật</span>}</div>
          </CastEditor>
        </div>
        {flowVersion === 'sky' && ev.videoMeta?.flags?.includes('SPLIT_CHILD') && ev.videoMeta.openingState && (
          <div style={{ margin: '4px 0', fontSize: 12, color: '#595959' }}><b>Mở đầu:</b> {ev.videoMeta.openingState}</div>
        )}
        <Paragraph style={{ fontSize: 14, lineHeight: 1.55, margin: '2px 0' }} editable={{ text: ev.action ?? '', onChange: (v) => onSaveEvent(ev.id, { action: v }), tooltip: 'Sửa mô tả — đây là nguồn AI dùng để dựng prompt' }}>
          {ev.action || '(describe what happens)'}
        </Paragraph>
        {bm.emotionalEvent && <div className="sb-emo">💥 {bm.emotionalEvent}</div>}
        {isV2 && (
          <div style={{ margin: '6px 0 8px' }}>
            <div style={{ font: '600 10px/1 var(--sb-mono)', letterSpacing: '.06em', textTransform: 'uppercase', color: '#8a92a0', marginBottom: 4 }}>
              Chuỗi nhịp · {(bm.actionBeats || []).length} nhịp
            </div>
            {beatSeq.map((it, k) => (it.kind === 'beat'
              ? (
                <div key={`b${k}`} style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                  <b>{it.beat.order}.</b> {it.beat.text}
                  {it.beat.direction && <span style={{ color: 'var(--sb-faint)' }}> — {it.beat.direction}</span>}
                  {(it.beat.sourceLines || []).length > 0 && <span style={{ color: 'var(--sb-faint)', fontSize: 10.5 }}> · dòng {it.beat.sourceLines.join(', ')}</span>}
                </div>
              ) : (
                <div key={`l${k}`} style={{ fontSize: 12.5, lineHeight: 1.5, paddingLeft: 16, fontStyle: 'italic' }}>
                  {it.line.speakerName || '—'}: “{it.line.textEn || ''}”
                  {it.unanchored && <Tooltip title="Câu này chưa biết nói xen sau nhịp nào (thoại cũ, chatbot viết, hoặc AI không trả được dòng nguồn)"><Tag color="orange" style={{ marginLeft: 6 }}>chưa neo nhịp</Tag></Tooltip>}
                </div>
              )))}
            {!beatSeq.length && <div style={{ fontSize: 12, color: 'var(--sb-faint)' }}>(event trống — chưa có nhịp nào)</div>}
          </div>
        )}
        {/* 261010 S5: khối Giai đoạn của plan sky nằm ngay dưới Chuỗi nhịp (trước đây ở ngoài cột nội dung, bị gập trong ADVANCED). Plan NULL: skyEventView = null ⇒ không render. */}
        <SkyPhasesBlock ev={ev} flowVersion={flowVersion} />
        {/* 261010 P3-1: storyboard của video sky nằm ngay dưới khối Giai đoạn. */}
        {isSkyPlan(flowVersion) && storyboard && <StoryboardBlock ev={ev} ctx={storyboard} />}
        {downgradeRecovery && (
          <div style={{ marginTop: 8, padding: 8, border: '1px solid #ffe7ba', borderRadius: 6, background: '#fffbe6' }}>
            <Text strong style={{ fontSize: 12 }}>Sau khi hạ 2.0 · việc tiếp theo</Text>
            <Space wrap style={{ marginTop: 6 }}>
              {downgradeRecovery.steps.map((step) => (
                <Tooltip key={step.key} title={step.note}>
                  <Button size="small" type={step.done ? 'default' : 'primary'} disabled={step.done || step.disabled || downgradeRecovery.busy} loading={downgradeRecovery.busy} onClick={() => downgradeRecovery.onRun(step.key)}>
                    {step.done ? '✓ ' : ''}{step.label}
                  </Button>
                </Tooltip>
              ))}
            </Space>
          </div>
        )}
        {/* Chip/tham chiếu rớt ở lượt gen gần nhất — clip vẫn ra, nhưng KHÔNG có ảnh của thứ người dùng đã chèn. */}
        {refWarnings.length > 0 && (
          <div className="sb-refwarn">
            {refWarnings.map((w, k) => <div key={k}>📎 {w}</div>)}
          </div>
        )}

        {shotCount > 0 && (
          <div className="sb-shots">
            <div className="sb-shots-h">🎞 {shotCount} shots</div>
            {(ev.shots || []).map((s, i) => (
              <div className="sb-shot" key={s.id}>
                <span className="i">{i + 1}</span>
                <span className="sz">{sizeVN(s.shotSize)}</span>
                <span className="desc">{shotDesc(s)}</span>
              </div>
            ))}
          </div>
        )}

        {(ev.dialogueLines || []).length > 0 && (
          <div style={{ marginTop: 10 }}>
            {(ev.dialogueLines || []).map((d, i) => (
              <DialogueRow key={d.id} d={d} castOpts={castOpts} simple
                onMode={(v) => setLine(i, { dialogueMode: v })}
                onSpeaker={(v) => setLine(i, { speakerName: v })}
                onText={(v) => setLine(i, { textEn: v })}
                onDelete={() => deleteLine(i)} />
            ))}
          </div>
        )}

        <Collapse ghost size="small" style={{ marginTop: 10 }} items={[{
          key: 'adv',
          label: <Text style={{ fontSize: 11.5, color: '#8a92a0', fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: '.04em' }}>⚙︎ ADVANCED — camera · prompt · split/merge · variant</Text>,
          children: (
    <div className={`sb-adv${dirty ? ' dirty' : ''}`}>
      <div className="sb-adv-head">
      <Space size={4} wrap>
        <Tooltip title="Event — one story beat = one video generation"><Text strong>E{ev.eventOrder}</Text></Tooltip>
        {dirty && <Tag color="warning">● unsaved</Tag>}
        {!simple && <Tooltip title={ev.kind === 'MULTI_SHOT' ? 'Multiple shots in one video' : 'Single shot'}><Tag color={ev.kind === 'MULTI_SHOT' ? 'geekblue' : 'default'}>{ev.kind}</Tag></Tooltip>}
        {/* Trọng lượng kịch — quyết định coverage (PEAK ≥2 shot) + linger (giữ lâu). AI hay đánh giá lệch
            (vd gán TRANSITION cho khoảnh khắc "mắt hoá vàng" chỉ vì nó bắc cầu sang cảnh sau) → sửa được ở đây.
            Đổi xong phải chạy lại ⏱️ Duration (và Directing nếu muốn coverage đổi theo). */}
        {simple
          ? (String(bm.dramaticWeight || '').toUpperCase() === 'PEAK' && <Tag color="volcano">🔥 PEAK</Tag>)
          : (
            <Tooltip title="Dramatic weight — PEAK: climax/hook (≥2 shots + linger) · TRANSITION: bridge, nothing changes · NORMAL: everything else">
              <Select size="small" style={{ width: 118 }} value={String(bm.dramaticWeight || 'NORMAL').toUpperCase()}
                onChange={(v) => onSaveEvent(ev.id, { beatMeta: { ...bm, dramaticWeight: v } })}
                options={[
                  { label: '🔥 PEAK', value: 'PEAK' },
                  { label: 'NORMAL', value: 'NORMAL' },
                  { label: '→ TRANSITION', value: 'TRANSITION' },
                ]} />
            </Tooltip>
          )}
        {!simple && bm.tactic && <Tooltip title="Tactic — the dramatic tactic (how the character gets what they want)"><Tag color="cyan">{bm.tactic}</Tag></Tooltip>}
        {!simple && bm.initiator && <Tooltip title="Character who initiates the beat"><Tag>starts: {bm.initiator}</Tag></Tooltip>}
        <Tooltip title="Estimated video duration"><Tag>⏱️ {ev.genDurationS ?? '?'}s</Tag></Tooltip>
        {!simple && (issues.length > 0 ? <Tooltip title="Continuity — some mismatch between shots (position/props/action)"><Tag color="red">⚠️ {issues.length} continuity</Tag></Tooltip> : <Tooltip title="Continuity — shots connect seamlessly"><Tag color="green">✓ continuity</Tag></Tooltip>)}
        {/* 260923 Epic2-P1 (Bước 8) — Kiểu prompt: Theo plan (X) · Tự nhiên · Kỹ thuật. Đổi khiến event EDITED
            rơi vào MISMATCH ⇒ hỏi trước (dựng lại sẽ ghi đè bản sửa tay, không tự động ở Phase 1). */}
        {!simple && (
          <Tooltip title="Mode prompt riêng của event này — 'Theo plan' = dùng mặc định của cả plan">
            <Select size="small" style={{ width: 150 }} value={ev.promptMode ?? '__PLAN__'}
              onChange={(v) => {
                const mode = v === '__PLAN__' ? null : (v as FilmPromptMode);
                const nextEffective = mode ?? planPromptMode;
                const willMismatch = ev.authorStatus === 'EDITED' && promptModeStateOf({ effectiveMode: nextEffective, authorPrompt: ev.authorPrompt, authorPromptMode: ev.authorPromptMode }) === 'MISMATCH';
                if (willMismatch) {
                  Modal.confirm({
                    title: `Prompt sửa tay đang là mode ${MODE_LABEL_VI[(ev.authorPromptMode as FilmPromptMode) || 'TECHNICAL']} — ở mode ${MODE_LABEL_VI[nextEffective]} sẽ chưa gen được tới khi dựng lại`,
                    content: 'Dựng lại sẽ ghi đè bản sửa tay hiện tại.',
                    okText: 'Đổi mode', cancelText: 'Huỷ',
                    onOk: () => onSetEventPromptMode(ev.id, mode),
                  });
                } else {
                  onSetEventPromptMode(ev.id, mode);
                }
              }}
              options={[
                { label: `Theo plan (${MODE_LABEL_VI[planPromptMode]})`, value: '__PLAN__' },
                { label: 'Tự nhiên', value: 'NATURAL' },
                { label: 'Kỹ thuật', value: 'TECHNICAL' },
              ]} />
          </Tooltip>
        )}
      </Space>
      <span style={{ marginLeft: 'auto' }} />
      {/* CRUD sửa ranh giới AI chia. Tách/gộp XOÁ duration+ledger+prompt → phải chạy lại pass. */}
      {!simple && (<Space size={2}>
        {/* 260923: event dạng chuỗi chưa tách được (tách theo nhịp là Phase 2) — ẩn nút, server cũng chặn 409. */}
        {shotCount > 1 && !isV2 && (
          <Popover trigger="click" placement="bottomRight" content={
            <div style={{ maxWidth: 260 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Split after which shot? Dialogue follows its shot.</div>
              <Space wrap size={4}>
                {Array.from({ length: shotCount - 1 }, (_, i) => i + 1).map((k) => (
                  <Button key={k} size="small" onClick={() => onSplit(ev.id, { keepShotCount: k })}>after shot {k}</Button>
                ))}
              </Space>
            </div>
          }>
            <Tooltip title="Split event — when an event is too dense for one generation (dialogue gets swallowed / shots get cut)">
              <Button size="small" type="text">✂️</Button>
            </Tooltip>
          </Popover>
        )}
        {/* E1-P2 (R8): event dạng chuỗi tách THEO NHỊP, chỉ ở ranh giới hợp lệ (hai phía không chung dòng nguồn). */}
        {isV2 && beatSplitPoints.length > 0 && (
          <Popover trigger="click" placement="bottomRight" content={
            <div style={{ maxWidth: 320 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Tách sau nhịp nào? Thoại đi theo nhịp đứng trước nó; shot hai nửa sẽ dàn lại.</div>
              <Space direction="vertical" size={4}>
                {beatSplitPoints.map((k) => (
                  <Button key={k} size="small" onClick={() => onSplit(ev.id, { keepBeatCount: k })}>
                    sau nhịp {k} — {String((bm.actionBeats || [])[k - 1]?.text || '').slice(0, 30)}
                  </Button>
                ))}
              </Space>
            </div>
          }>
            <Tooltip title="Tách event dạng chuỗi theo nhịp — chỉ ở ranh giới hợp lệ">
              <Button size="small" type="text">✂️</Button>
            </Tooltip>
          </Popover>
        )}
        {canMergeNext && (
          <Popconfirm title="Merge with the next event?" description="Joins action + shots + dialogue. Duration/ledger/prompt will be cleared." onConfirm={() => onMergeNext(ev.id)} okText="Merge" cancelText="Cancel">
            <Tooltip title="Merge with the event below — when the AI over-splits a moment"><Button size="small" type="text">⬇️</Button></Tooltip>
          </Popconfirm>
        )}
        <Tooltip title="Insert an empty event right after"><Button size="small" type="text" onClick={() => onInsertAfter(ev.id)}>＋</Button></Tooltip>
        <Popconfirm title="Delete this event?" description="Deletes its shots + dialogue too." onConfirm={() => onDeleteEvent(ev.id)} okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}>
          <Tooltip title="Delete event"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
        </Popconfirm>
      </Space>)}
      </div>
      {/* Advanced: action + emotional đã có ở card storyboard → bỏ trùng, chỉ giữ dialogue-add + shot-edit + clip + prompt. */}
      {!simple && issues.length > 0 && <div style={{ fontSize: 11, color: '#cf1322', marginBottom: 8 }}>{issues.map((i, k) => <div key={k}>⚠️ {i}</div>)}</div>}
      {/* Cảnh báo prompt — màu cam để không lẫn với continuity (đỏ): chúng dẫn tới hành động khác nhau. */}
      {promptWarns.length > 0 && (
        <div style={{ fontSize: 11, color: '#d46b08', marginBottom: 8 }}>
          {promptWarns.map((w, k) => <div key={k}>🕐 {w}</div>)}
        </div>
      )}
      <div style={{ marginBottom: 6 }}>
          <div style={{ font: '600 10px/1 var(--sb-mono)', letterSpacing: '.06em', textTransform: 'uppercase', color: '#8a92a0', marginBottom: 6 }}>Dialogue</div>
          {(ev.dialogueLines || []).map((d, i) => (
            <DialogueRow key={d.id} d={d} castOpts={castOpts} simple={simple} shotOrder={lineTaken.get(i + 1)?.order}
              onMode={(v) => setLine(i, { dialogueMode: v })}
              onSpeaker={(v) => setLine(i, { speakerName: v })}
              onText={(v) => setLine(i, { textEn: v })}
              onDelete={() => deleteLine(i)} />
          ))}
          <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addLine} style={{ marginTop: 2 }}>Add line</Button>
        </div>
      {!simple && (
      <div style={{ marginTop: 4 }}>
        {(ev.shots || []).map((s, i) => (
          <ShotCard key={s.id} shot={s} onSave={onSaveShot} onDelete={onDeleteShot}
            onMove={(id, dir) => onMoveShot(ev, id, dir)} canUp={i > 0} canDown={i < ev.shots.length - 1} cast={cast}
            dirty={(dirtyShotIds || []).includes(s.id)} lines={ev.dialogueLines || []} lineTaken={lineTaken} />
        ))}
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => onAddShot(ev.id)} style={{ marginTop: 2 }}>
          {/* v1: ngân sách 1 event = 3 shot (~8s) — vượt thì khuyên tách. v2 (260923 E1-P3, S8): ngân sách MỀM theo nhịp
              (`beatTiming.shotBudget`), chỉ cảnh báo, KHÔNG khuyên tách (tách = xoá shot hai nửa). */}
          Add shot {addShotHint(ev)}
        </Button>
      </div>
      )}
      {/* ── Candidate clip ── */}
      <div style={{ marginTop: 8, borderTop: '1px dashed #eee', paddingTop: 6 }}>
        <Space wrap size={4}>
          {genBtn(false, <Button size="small" type="primary" icon={<VideoCameraOutlined />} loading={busy}>🎬 Gen clip</Button>)}
          {(ev.clips || []).length > 0 && genBtn(true, <Button size="small">＋ variant</Button>)}
          {tuningBtn()}
          {!simple && ev.authorStatus && <Tooltip title="Prompt status: AUTHORED = AI wrote it · EDITED = edited by hand"><Tag color={ev.authorStatus === 'EDITED' ? 'gold' : 'blue'}>prompt {ev.authorStatus}</Tag></Tooltip>}
          {/* 260923 Epic2-P1 (Bước 8): nhãn mode hiệu lực + hậu tố đè/ghim + tag trạng thái lệch/thiếu. */}
          <Tooltip title="Mode prompt hiệu lực của event này (Kỹ thuật = khung khối camera/giây; Tự nhiên = chữ kể chuyện)">
            <Tag color={effectivePromptMode === 'NATURAL' ? 'purple' : 'default'}>{modeLabel.modeText}{modeLabel.suffix}</Tag>
          </Tooltip>
          {modeLabel.stateTag && <Tag color={modeLabel.stateTag.color}>{modeLabel.stateTag.text}</Tag>}
          {!(ev.clips || []).length && <Text type="secondary" style={{ fontSize: 11 }}>not generated</Text>}
        </Space>
        {(ev.clips || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
            {(ev.clips || []).map((c) => {
              const active = c.id === ev.selectedClipId;
              return (
                <div key={c.id} style={{ border: active ? '2px solid #1677ff' : '1px solid #eee', borderRadius: 4, padding: 4, width: 156 }}>
                  {c.status === 'DONE' && c.videoUrl
                    ? <video src={c.videoUrl} controls style={{ width: 146, borderRadius: 2 }} />
                    : <div style={{ width: 146, height: 82, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                        {c.status === 'PENDING'
                          ? <Spin size="small" />
                          : clipFailInfo(c)
                            ? <Tooltip title={<FailTip info={clipFailInfo(c)!} raw={c.error} />} overlayStyle={{ maxWidth: 420 }}>
                                <Tag color="error" style={{ cursor: 'help', whiteSpace: 'normal', textAlign: 'center', margin: 0 }}>✕ {clipFailInfo(c)!.label}</Tag>
                              </Tooltip>
                            : <Tag color={CLIP_STATUS_COLOR[c.status || 'FAILED'] || 'default'}>{c.status}</Tag>}
                      </div>}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                    <Text style={{ fontSize: 10 }}>{c.variantLabel || 'v1'} {c.durationS ? `· ${c.durationS}s` : ''}</Text>
                    {c.status === 'DONE' && <Tooltip title="Pick this one for the final cut"><Button size="small" type={active ? 'primary' : 'default'} onClick={() => onSelectClip(ev.id, c.id)}>{active ? '✓ selected' : 'Select'}</Button></Tooltip>}
                  </div>
                  {/* 260813: trước đây cắt cứng 60 ký tự — đúng đoạn "Seedance task cgt-… FAILED: {" nên mã lỗi
                      (thứ duy nhất đáng đọc) luôn bị cắt mất. Giờ hiện mã Seedance + gợi ý ngắn; nguyên văn ở tooltip. */}
                  {clipFailInfo(c) && (
                    <div style={{ fontSize: 10, color: '#cf1322', lineHeight: 1.35 }}>
                      {clipFailInfo(c)!.code && <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600, wordBreak: 'break-all' }}>{clipFailInfo(c)!.code}</div>}
                      {clipFailInfo(c)!.hint}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                    {c.prompt
                      ? <Popover trigger="click" title="Seedance prompt that generated this video" placement="topLeft"
                          content={<div style={{ width: 420, maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 11 }}>{c.prompt}</div>}>
                          <span style={{ fontSize: 10, color: '#1677ff', cursor: 'pointer' }}>📝 prompt</span>
                        </Popover>
                      : <span style={{ fontSize: 10, color: '#ccc' }}>—</span>}
                    <Popconfirm title="Delete this variant?" okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }} onConfirm={() => onDeleteClip(c.id)}>
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Delete variant" style={{ height: 18, padding: '0 4px' }} />
                    </Popconfirm>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* ── Info panel (prompt / reference / ledger) — parity với beat cũ. P2: ẩn ở chế độ Đơn giản. ── */}
      {!simple && (() => {
        // Khớp KHÔNG phân biệt hoa/thường: Location Bank lưu tên viết HOA ("LUXURY PENTHOUSE BEDROOM" — do
        // breakdown trích từ scene heading), còn scene.location giữ nguyên dạng ("Luxury Penthouse Bedroom").
        // So sánh === khiến UI báo "✗ chưa gen nền" dù plate đã gen xong (backend vốn đã toLowerCase khi khớp).
        // 260807: chưa đủ — tên còn khác cả CHỮ ("MORTAIN MANSION" vs "Mortain Mansion - Party Room"), nên
        // phải khớp rộng đúng luật backend dùng lúc gen (`resolveLocationByName`).
        const presentNames = Array.from(new Set((ev.shots || []).flatMap((s) => s.presence?.visible || [])));
        const refChar = (n: string) => refBank?.characters.find((c) => castNameMatches(c, n));
        const locPlate = resolveLocationByName(sceneLocation, refBank?.locations) || undefined;
        const ledger = ev.continuityLedger?.after;
        return (
          <Collapse size="small" ghost style={{ marginTop: 6 }} items={[
            {
              key: 'prompt', label: <Text style={{ fontSize: 12 }}>📝 Prompt Seedance {ev.authorStatus && <Tag color={ev.authorStatus === 'EDITED' ? 'gold' : 'blue'}>{ev.authorStatus}</Tag>}</Text>,
              children: ev.authorPrompt
                ? <div>
                    <PromptChipEditor value={ev.authorPrompt} bank={refBank} engine={engine} onSave={(v) => {
                      // 260923 Epic2-P1 (L5) — event đang MISMATCH: Lưu là chọn snapshot mới = mode hiệu lực
                      // hiện tại. Hỏi trước — "sửa một ký tự" không nên là cửa lách vô tình (N6).
                      if (promptModeState === 'MISMATCH') {
                        Modal.confirm({
                          title: `Lưu chữ này làm prompt mode ${MODE_LABEL_VI[effectivePromptMode]}?`,
                          content: 'Prompt đang lưu hiện là mode khác — Lưu sẽ đổi snapshot sang mode hiệu lực của event.',
                          okText: 'Lưu', cancelText: 'Huỷ',
                          onOk: () => onSavePrompt(ev.id, v),
                        });
                      } else {
                        onSavePrompt(ev.id, v);
                      }
                    }} />
                    <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>{genBtn(true, <Button size="small" icon={<VideoCameraOutlined />} loading={busy}>Gen with this prompt</Button>)}
                      <Button size="small" onClick={() => setEngineViewId(ev.id)}>Xem bản gửi engine</Button>
                      <EnginePromptModal eventId={engineViewId === ev.id ? engineViewId : null} onClose={() => setEngineViewId(null)} /></div>
                  </div>
                : <Text type="secondary" style={{ fontSize: 11 }}>No prompt authored yet — click "✍️ Author prompt" in the toolbar.</Text>,
            },
            {
              key: 'ref', label: <Text style={{ fontSize: 12 }}>🖼️ Reference (@Image = who/what)</Text>,
              children: <div style={{ fontSize: 11 }}>
                {presentNames.length === 0 && <Text type="secondary">No characters present.</Text>}
                {presentNames.map((n) => {
                  const r = refChar(n);
                  return <div key={n} style={{ marginBottom: 2 }}>
                    <Text strong>{n}</Text>{' '}
                    {r?.kind === 'CROWD' ? <Tag color="blue">đám đông — không cần mặt</Tag> : r ? <Space size={2}>
                      <Tag color={r.hasDC ? 'green' : 'red'}>{r.hasDC ? '✓ face (DC)' : '✗ face'}</Tag>
                      <Tag color={r.hasOutfit ? 'green' : 'orange'}>{r.hasOutfit ? '✓ outfit' : '✗ outfit'}</Tag>
                      <Tag color={r.hasVoice ? 'green' : 'default'}>{r.hasVoice ? '✓ voice' : '– voice'}</Tag>
                    </Space> : <Tag color="red">✗ not in bank</Tag>}
                  </div>;
                })}
                {sceneLocation && <div style={{ marginTop: 4 }}>
                  <Text strong>📍 {sceneLocation}</Text>{' '}
                  <Tag color={locPlate?.hasPlate ? 'green' : 'red'}>{locPlate?.hasPlate ? '✓ has background plate' : '✗ no background plate'}</Tag>
                </div>}
              </div>,
            },
            {
              key: 'ledger', label: <Text style={{ fontSize: 12 }}>📗 Continuity ledger (state after event)</Text>,
              children: ledger
                ? <div style={{ fontSize: 11 }}>
                    {(ledger.characters || []).map((c, k) => <div key={k} style={{ marginBottom: 2 }}>
                      <Text strong>{c.name}</Text>: {c.position || '?'}{c.holding?.length ? ` · holding: ${c.holding.join(', ')}` : ''}{c.mood ? ` · ${c.mood}` : ''}
                    </div>)}
                    {ledger.setting && <div style={{ marginTop: 4, color: '#888' }}>🏙️ {ledger.setting.location} · {ledger.setting.timeOfDay}</div>}
                  </div>
                : <Text type="secondary" style={{ fontSize: 11 }}>No ledger yet — run "check continuity".</Text>,
            },
          ]} />
        );
      })()}
    </div>
          ),
        }]} />
      </div>
    </div>
  );
};

interface VerifyResult {
  lint: { findings: Array<{ severity: 'error' | 'warn'; code: string; ref: string; message: string }>; counts: { error: number; warn: number } };
  judge: { overall: number; scores: Record<string, number>; flaws: Record<string, string[]> };
}

// ── Lớp nháp (260715 Epic 1 P1): gom sửa-tay 1 lượt → Lưu 1 lần → batch-edit. Undo/Huỷ nháp. ──
type DlgLite = { speakerName?: string | null; textEn?: string | null; dialogueMode: FilmDialogueMode; afterActionBeatOrder?: number | null }; // 260923: + neo nhịp
interface OpsMap {
  ev: Record<string, { action?: string; kind?: FilmBeatKind; genDurationS?: number | null; beatMeta?: any }>;
  prompt: Record<string, string | null>;
  dlg: Record<string, DlgLite[]>;
  sh: Record<string, Record<string, unknown>>;
  /** E4-P2: op CẤU TRÚC — mảng CÓ THỨ TỰ (không giao hoán, khác 4 map trên). */
  struct: StructOp[];
}
interface EditState { draft: EventPlan; ops: OpsMap; }
const emptyOps = (): OpsMap => ({ ev: {}, prompt: {}, dlg: {}, sh: {}, struct: [] });
const cloneJson = <T,>(v: T): T => JSON.parse(JSON.stringify(v));
let TMP_LINE = 0;
const tmpLineId = () => `tmp-${++TMP_LINE}`; // id tạm cho dòng thoại mới trong nháp (chỉ để React key; server tự sinh id thật khi Lưu)
let TMP_EV = 0;
const tmpEventId = () => `tmp-ev-${++TMP_EV}`; // E4-P2: id nháp của event MỚI (split/insert) — server resolve sang id thật khi Lưu
const opsCount = (o: OpsMap) => Object.keys(o.ev).length + Object.keys(o.prompt).length + Object.keys(o.dlg).length + Object.keys(o.sh).length + o.struct.length;
const findEvent = (p: EventPlan, id: string): FilmEvent | undefined => { for (const s of p.scenes) { const e = s.events.find((x) => x.id === id); if (e) return e; } return undefined; };
const findShot = (p: EventPlan, id: string): { shot: FilmShot; eventId: string } | undefined => {
  for (const s of p.scenes) for (const e of s.events) { const sh = (e.shots || []).find((x) => x.id === id); if (sh) return { shot: sh, eventId: e.id }; }
  return undefined;
};
const buildBatchOps = (o: OpsMap): BatchEditOp[] => {
  const arr: BatchEditOp[] = [];
  // E4-P2: op CẤU TRÚC đi TRƯỚC, đúng thứ tự user thao tác (server áp struct→field, xem E4-P1).
  for (const st of o.struct) arr.push(st as unknown as BatchEditOp);
  for (const [eventId, d] of Object.entries(o.ev)) arr.push({ type: 'updateEvent', eventId, ...d });
  for (const [eventId, authorPrompt] of Object.entries(o.prompt)) arr.push({ type: 'updatePrompt', eventId, authorPrompt });
  for (const [eventId, lines] of Object.entries(o.dlg)) arr.push({ type: 'replaceDialogue', eventId, lines });
  for (const [shotId, d] of Object.entries(o.sh)) arr.push({ type: 'updateShot', shotId, ...d });
  return arr;
};

const EventPlanTab: React.FC<{ planId: string; onBack?: () => void; planName?: string; planStatus?: string; chapterIds?: string[]; /** 261010 P2a: mở khung mới 9 bước (chỉ plan Sky; UI cũ giữ nguyên) */ onOpenWorkflow?: () => void; /** 261030: `?fevent=` — cuộn tới thẻ video này và nháy viền (đích «Tách video» từ bước ④ của khung 4 bước) */ focusEventId?: string }> = ({ planId, onBack, planName, planStatus, chapterIds, onOpenWorkflow, focusEventId }) => {
  // 261030 C1 — plan layout four gen video ở bước ④ (hộp giá): ẩn nút gen cũ, chặn hàm gen. Context có plan đúng id thì dùng; không thì đọc riêng một lần.
  const filmCtx = useFilmPlanOptional();
  const [fetchedLayout, setFetchedLayout] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    setFetchedLayout(undefined);
    if (filmCtx?.plan?.id === planId) return undefined;
    let live = true;
    Promise.resolve().then(() => filmApi.getPlan(planId)).then((p) => { if (live) setFetchedLayout(p?.workflowLayout ?? null); }).catch(() => undefined);
    return () => { live = false; };
  }, [planId, filmCtx?.plan?.id]);
  const layoutFour = isLayoutFourPlan(planId, filmCtx?.plan, fetchedLayout);
  const [data, setData] = useState<EventPlan | null>(null);   // bản SERVER (đã lưu)
  const [edit, setEdit] = useState<EditState | null>(null);   // working copy + ops + dirty (nháp)
  // 261030: mở thẳng video đích (`?fevent=`): đợi dữ liệu tải xong rồi cuộn tới thẻ video và nháy viền một lần.
  const focusedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusEventId || !edit || focusedRef.current === focusEventId) return undefined;
    const timer = window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-event-id="${focusEventId}"]`);
      if (!el) return;
      focusedRef.current = focusEventId;
      el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      el.classList.add('sb-focus-flash'); window.setTimeout(() => el.classList.remove('sb-focus-flash'), 3500);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [focusEventId, edit]);
  const [history, setHistory] = useState<EditState[]>([]);    // snapshot cho Undo
  // Epic 2 Phase 2: tự re-author prompt cho event out-of-date NGAY sau khi Lưu (khỏi bấm Sync). Tắt được nếu ngại chi phí LLM.
  const [autoSync, setAutoSync] = useState(() => localStorage.getItem('filmAutoSync') !== 'off');
  const changeAutoSync = (v: boolean) => { setAutoSync(v); localStorage.setItem('filmAutoSync', v ? 'on' : 'off'); };
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [verify, setVerify] = useState<VerifyResult | null>(null);
  const [bankOpen, setBankOpen] = useState(false);
  const [bankTab, setBankTab] = useState<string | undefined>(undefined);
  // Trạng thái tủ đồ theo cảnh — stepper cần biết để nói "còn N dòng chờ duyệt" thay vì im lặng.
  const [sceneLooks, setSceneLooks] = useState<{ status: string }[]>([]);
  const [asmOpen, setAsmOpen] = useState(false);
  // 260923 Epic2-P1 Bước 8 — modal "Đổi kiểu prompt" (dryRun xem-trước-ảnh-hưởng rồi mới áp).
  // 260923 Epic2-P3 (U4/Bước 3) — `rebuild`: ô "Dựng lại ngay N prompt AI" mặc định BẬT.
  const [modeModal, setModeModal] = useState<{ target: FilmPromptMode; loading: boolean; rebuild: boolean; preview?: { pinned: number[]; mismatchAfter: number[]; missingAfter: number[]; okAfter: number } } | null>(null);
  // P1c-1: preview là read-only; chọn candidate luôn bắt đầu trống, không nhớ selection cũ.
  const [downgradeModal, setDowngradeModal] = useState<{ eventId: string; loading: boolean; accepting: boolean; preview?: FilmDowngradePreview; selectedKeepBeatCount?: number; errorCode?: string; errorText?: string } | null>(null);
  const [downgradeChildIds, setDowngradeChildIds] = useState<string[] | null>(null);
  const [chatOpen, setChatOpen] = useState(false); // Epic 3: Drawer chatbot trợ lý
  // 260722 copilot E3-P1: PHIÊN chat sống ở đây (không ở panel) → đóng/mở Drawer giữ nguyên lịch sử,
  // health-check chỉ chạy 1 lần/plan. Đổi plan → reset phiên (chat gắn với plan, không lẫn).
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const [currentStage, setCurrentStage] = useState<string | undefined>(undefined);
  useEffect(() => { setChatMessages([]); }, [planId]);
  const editorRef = useRef<HTMLDivElement>(null);

  const nOps = edit ? opsCount(edit.ops) : 0;
  const isDirty = nOps > 0;
  const isDirtyRef = useRef(false);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

  // Nạp bản server → khởi tạo nháp SẠCH (dùng cho load đầu + sau mọi thao tác server làm đổi plan).
  const applyServerPlan = useCallback((p: EventPlan) => {
    setData(p);
    setEdit({ draft: cloneJson(p), ops: emptyOps() });
    setHistory([]);
  }, []);

  const reload = useCallback(async () => {
    try { applyServerPlan(await filmApi.getEventPlan(planId)); }
    catch (e: any) { message.error(`Load failed: ${e?.response?.data?.message || e?.message}`); }
    finally { setLoading(false); }
  }, [planId, applyServerPlan]);

  useEffect(() => { setLoading(true); reload(); }, [reload]);

  const loadSceneLooks = useCallback(async () => {
    try { setSceneLooks(await filmApi.sceneLooks(planId)); }
    catch { /* im lặng: thiếu số liệu tủ đồ không đáng làm hỏng cả tab */ }
  }, [planId]);
  useEffect(() => { loadSceneLooks(); }, [loadSceneLooks]);

  // Poll khi có clip đang gen (PENDING) → reload tới khi xong. BỎ QUA khi đang có nháp (đừng wipe draft user đang sửa).
  useEffect(() => {
    if (!data) return;
    const pending = data.scenes.some((s) => s.events.some((e) => (e.clips || []).some((c) => c.status === 'PENDING')));
    if (!pending) return;
    const t = setInterval(() => { if (!isDirtyRef.current) reload(); }, 10000);
    return () => clearInterval(t);
  }, [data, reload]);

  // E6-P1: việc nền đang chạy — đọc từ SERVER, không suy từ state của tab.
  // Vì sao phải hỏi server: `busy` chết theo tab (reload là mất), không thấy tab khác, và không biết gì
  // về việc do người khác bấm. Đây là thứ khiến người dùng bấm lại vì tưởng chưa chạy — mỗi lần là tiền.
  /**
   * E6-P2 · sửa phạm vi 260819 — tuỳ chọn gen là của TỪNG CLIP, không phải của cả tab.
   *
   * Trước đây đây là MỘT biến dùng chung, lại nhớ ở localStorage: nút ⚙️ đứng ở từng clip và ghi
   * "Gen clip này bằng", nhưng đổi model ở một clip là đổi cho mọi clip còn lại — và còn nhớ sang cả
   * phiên sau. Ai thử `pro · 1080p · 3 bản` cho một cảnh rồi quên tắt thì mọi lượt gen lẻ về sau đều
   * đắt gấp mấy lần mà không có gì nhắc. Nay mỗi event giữ lựa chọn riêng và KHÔNG lưu qua phiên:
   * mở lại tab là mọi clip về mặc định rẻ.
   */
  const [tuningByEvent, setTuningByEvent] = useState<Record<string, GenTuning>>({});
  const tuningOf = useCallback((eventId: string): GenTuning => tuningByEvent[eventId] || {}, [tuningByEvent]);
  const saveTuningFor = useCallback((eventId: string, t: GenTuning) => {
    setTuningByEvent((m) => ({ ...m, [eventId]: t }));
  }, []);

  const [jobs, setJobs] = useState<FilmRunningJob[]>([]);
  // Việc nền CHẾT gần đây + id đã được đóng. Việc nền trả 202 nên lỗi của nó không về theo response được:
  // không hiện ở đây thì băng "đang chạy" chỉ lặng lẽ biến mất (đo trên prod: author prompt chết 12 lần vì
  // thiếu plate, người dùng không hề được báo và cứ bấm lại).
  const [failures, setFailures] = useState<FilmFailedJob[]>([]);
  const [dismissedJobs, setDismissedJobs] = useState<string[]>(() => loadDismissedJobs());
  /**
   * 260928 — job nền KẾT THÚC nhưng thẻ event vẫn đứng ở dữ liệu CŨ ("CHƯA CÓ PROMPT", nút Generate khoá)
   * tới khi F5. Gốc: `authorPrompts()`/`runPass()` chỉ `setTimeout(reload, 2-3s)` MỘT LẦN — job author thật
   * chạy lâu hơn vài giây nên lượt reload đó luôn rơi vào lúc job CHƯA xong. Vá ở ĐÂY (không phải từng nút):
   * so danh sách job đang chạy giữa hai lượt poll — job nào có ở lượt TRƯỚC mà mất ở lượt NÀY là vừa xong
   * (done hoặc failed) ⇒ tự `reload()` dữ liệu plan. Áp cho MỌI loại job nền (author/dialogue/breakdown/
   * continuity/durations/script-passes), không riêng "Dựng prompt". Bỏ khi đang có nháp (`isDirtyRef`) —
   * cùng luật với poll clip PENDING phía trên, đừng wipe draft người dùng đang sửa.
   */
  const prevJobIdsRef = useRef<Set<string> | null>(null);
  const loadJobs = useCallback(async () => {
    try {
      const r = await filmApi.getPlanJobs(planId);
      const nextJobs = r.jobs || [];
      const finished = jobsFinishedSincePoll(prevJobIdsRef.current, nextJobs);
      prevJobIdsRef.current = new Set(nextJobs.map((j) => j.id));
      setJobs(nextJobs);
      setFailures(r.failures || []);
      if (finished && !isDirtyRef.current) reload();
      return r; // 261010 P2-1: PositionMapPanel hỏi NGAY sau 202 và cần thấy kết quả
    } catch { /* im lặng: thiếu băng job không đáng làm hỏng cả tab */ return undefined; }
  }, [planId, reload]);
  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => {
    // Còn việc chạy thì hỏi dồn (5s); rảnh thì thưa (20s) để vẫn bắt được việc do tab/người khác khởi động.
    const t = setInterval(loadJobs, jobs.length ? 5000 : 20000);
    return () => clearInterval(t);
  }, [loadJobs, jobs.length]);
  /** Có việc loại này đang chạy không? (khoá nút ở cấp plan) */
  /**
   * GỘP các lần chết GIỐNG NHAU (cùng loại việc + cùng lý do) thành MỘT băng kèm số lần.
   *
   * 🔴 Vì sao: cửa sổ `failures` là 180 phút, nên sau khi sửa xong nguyên nhân người dùng vẫn thấy cả xâu lỗi
   * lịch sử xếp chồng (đo thật: 6 băng y hệt nhau, "31′ / 70′ / 90′ / 91′ / 98′ / 163′ trước") và tưởng vẫn
   * đang lỗi. Một băng "thất bại 6 lần — gần nhất 31′ trước" nói đúng chuyện đó mà không phủ kín màn hình.
   * Đóng băng gộp thì tắt cả nhóm. Giữ tối đa 3 nhóm gần nhất — cũ hơn nữa không còn giúp gì.
   */
  const groupedFailures = useMemo(() => {
    // 261010 P2-1: `PUT .../position-maps/links` chạy dưới khoá FILM_POSITION_SETUPS nên lỗi kiểm tra của nó (người dùng đã nhận ngay trên response) lọt vào đây — bỏ.
    const live = failures.filter((f) => !dismissedJobs.includes(f.id) && !isPutLinksNoise(f));
    const byKey = new Map<string, { key: string; type: string; phase?: string; eventId?: string; note: string; minutesAgo: number; count: number; ids: string[] }>();
    for (const f of live) {
      const key = `${f.type}|${f.phase || ''}|${f.eventId || ''}|${f.note}`;
      const g = byKey.get(key);
      if (g) { g.count += 1; g.ids.push(f.id); g.minutesAgo = Math.min(g.minutesAgo, f.minutesAgo); }
      else byKey.set(key, { key, type: f.type, phase: f.phase, eventId: f.eventId, note: f.note, minutesAgo: f.minutesAgo, count: 1, ids: [f.id] });
    }
    return Array.from(byKey.values()).sort((a, b) => a.minutesAgo - b.minutesAgo).slice(0, 3);
  }, [failures, dismissedJobs]);

  const jobRunning = (type: string) => jobs.some((j) => j.type === type);
  /** Event này đang gen clip? — tính cả job của tab khác lẫn clip PENDING còn trong DB. */
  const eventGenerating = useCallback((eventId: string) =>
    jobs.some((j) => j.eventId === eventId && (j.type === 'FILM_GEN_CLIP' || j.type === 'FILM_REGEN_CLIP')),
  [jobs]);

  // ── 261010 P3-1 — Storyboard Sky: tải MỘT LẦN mỗi plan (khuôn PositionMapPanel), truyền xuống thẻ video qua map eventId → storyboard. ──
  // Chỉ plan sky mới gọi (plan khác ⇒ 409 NOT_SKY_PLAN). Sơ đồ (links + tên) nạp cùng để ô "Sơ đồ" của bảng khung chỉ liệt kê map hợp lệ.
  const isSkyData = !!data && isSkyPlan(data.plan.flowVersion);
  const [sbByEvent, setSbByEvent] = useState<Record<string, Storyboard>>({});
  const [sbPos, setSbPos] = useState<StoryboardCtx['pos']>(null);
  const [sbState, setSbState] = useState<{ loaded: boolean; loading: boolean; error: string | null }>({ loaded: false, loading: false, error: null });
  useEffect(() => { setSbByEvent({}); setSbPos(null); setSbState({ loaded: false, loading: false, error: null }); }, [planId]);
  const reloadStoryboards = useCallback(async () => {
    setSbState((s) => ({ ...s, loading: true }));
    try {
      const [list, pos] = await Promise.all([filmApi.getStoryboards(planId), filmApi.getPositionMaps(planId).catch(() => null)]);
      setSbByEvent(storyboardsByEvent(list));
      setSbPos(pos ? { links: pos.links.valid, maps: pos.maps.map((m) => ({ id: m.id, name: m.name })) } : null);
      setSbState({ loaded: true, loading: false, error: null });
    } catch (e: any) {
      setSbState((s) => ({ ...s, loaded: true, loading: false, error: storyboardErrorText(e, 'Tải storyboard') }));
    }
  }, [planId]);
  // Cấu trúc đổi (thời lượng gen, tập shot, tập giai đoạn) ⇒ `drift` có thể đổi ⇒ nạp lại. Chạy cả lần mount.
  const skySig = useMemo(() => (data && isSkyData ? eventsSignature(data.scenes.flatMap((sc) => sc.events)) : ''), [data, isSkyData]);
  useEffect(() => { if (isSkyData) reloadStoryboards(); }, [isSkyData, reloadStoryboards, skySig]);
  // Job storyboard vừa XONG (có ở lượt poll trước, mất ở lượt này) ⇒ nạp lại. KHÔNG phụ thuộc isDirtyRef: khối này không phải nháp của editor plan.
  const prevSbJobs = useRef<FilmRunningJob[] | null>(null);
  useEffect(() => {
    const now = jobs.filter((j) => j.type === STORYBOARD_JOB);
    if (isSkyData && finishedStoryboardJobs(prevSbJobs.current, now).length) reloadStoryboards();
    prevSbJobs.current = now;
  }, [jobs, isSkyData, reloadStoryboards]);

  // Cảnh báo trước khi rời trang nếu còn nháp chưa lưu.
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (isDirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, []);

  // Ghi 1 thay đổi vào nháp (đẩy snapshot cũ vào history cho Undo). KHÔNG gọi API.
  const mutate = (fn: (e: EditState) => void) => {
    if (!edit) return;
    setHistory((h) => [...h, edit]);
    const next = cloneJson(edit);
    fn(next);
    setEdit(next);
  };
  // Chặn thao tác server (pass/gen/CRUD) khi còn nháp — tránh chạy trên data cũ rồi reload wipe mất nháp.
  const ensureClean = (what: string): boolean => {
    if (isDirty) { message.warning(`${nOps} unsaved change${nOps > 1 ? 's' : ''} — click 💾 Save or Discard before ${what}`); return false; }
    return true;
  };

  /** P1b: server đổi engine + giây nguyên tử. Thành công mới bỏ model chọn tay để không gửi chéo họ model. */
  const changeEventVideoEngine = async (eventId: string, engine: import('../../types/film').FilmVideoEngine, acceptEditedPrompt = false): Promise<'changed' | 'promptEdited' | 'needsSplit' | 'failed'> => {
    if (!ensureClean('changing video engine')) return 'failed';
    try {
      await filmApi.setEventVideoEngine(eventId, engine, acceptEditedPrompt);
      setTuningByEvent((current) => {
        const tuning = current[eventId];
        if (!tuning?.model) return current;
        const { model: _model, ...rest } = tuning;
        return { ...current, [eventId]: rest };
      });
      await reload();
      message.success(`Đã đổi engine sang ${engine === 'SEEDANCE_25' ? '2.5' : '2.0'}`);
      return 'changed';
    } catch (e: any) {
      const rawMessage = e?.response?.data?.message || e?.message || 'Đổi engine thất bại';
      if (e?.response?.data?.code === 'NEEDS_SPLIT') return 'needsSplit';
      message.error(rawMessage);
      return e?.response?.data?.code === 'PROMPT_EDITED_TIMING' && !acceptEditedPrompt ? 'promptEdited' : 'failed';
    }
  };

  const openDowngrade = async (eventId: string) => {
    if (!ensureClean('opening downgrade preview')) return;
    setDowngradeModal({ eventId, loading: true, accepting: false });
    try {
      const preview = await filmApi.getDowngradePreview(eventId);
      setDowngradeModal({ eventId, loading: false, accepting: false, preview });
    } catch (e: any) {
      const code = e?.response?.data?.code;
      setDowngradeModal({ eventId, loading: false, accepting: false, errorCode: code, errorText: downgradeErrorText(code) });
    }
  };
  const reloadDowngradePreview = () => { if (downgradeModal) openDowngrade(downgradeModal.eventId); };
  const acceptDowngrade = async () => {
    if (!downgradeModal?.preview || downgradeModal.selectedKeepBeatCount == null) return;
    const { eventId, preview, selectedKeepBeatCount } = downgradeModal;
    setDowngradeModal((m) => m ? { ...m, accepting: true } : m);
    try {
      const accepted = await filmApi.acceptDowngrade(eventId, selectedKeepBeatCount, preview.previewHash);
      setDowngradeChildIds([accepted.keepId, accepted.newId]);
      await reload();
      message.success('Đã tách atomically thành hai video 2.0. Hãy chạy lần lượt bốn bước gỡ bên dưới.');
      setDowngradeModal(null);
    } catch (e: any) {
      const code = e?.response?.data?.code;
      setDowngradeModal((m) => m ? { ...m, accepting: false, errorCode: code, errorText: downgradeErrorText(code) } : m);
    }
  };

  /**
   * Chạy một pass kịch bản. Từ 260806 các pass này trả **202 (nền)**, không còn trả số liệu:
   * gateway cắt request ở 180 giây (đo thật) nên endpoint đồng bộ dài luôn "xong ở server, lỗi ở client".
   * Bấm → server đặt khoá → băng "đang chạy" (poll `getPlanJobs`) → dữ liệu tự hiện khi poll lại plan;
   * chết thì băng đỏ nói lý do. Nhánh lỗi vẫn `reload()` vì server có thể đã ghi xong một phần.
   */
  /**
   * Chờ một loại việc nền của plan chạy xong (dùng khi bước SAU phụ thuộc bước TRƯỚC).
   *
   * 🔴 Cần vì từ 260806 các pass trả 202: chỗ nào trước đây `await` được kết quả (ví dụ dựng lại continuity
   * trước khi author prompt) thì nay phải chờ job, nếu không sẽ author trên ledger CŨ — sai âm thầm.
   * `sawRunning` để không kết luận "xong" khi job chưa kịp xuất hiện trong danh sách.
   */
  const waitForPlanJob = async (type: string, maxMs = 10 * 60_000): Promise<'done' | 'failed' | 'timeout'> => {
    const t0 = Date.now();
    let sawRunning = false;
    while (Date.now() - t0 < maxMs) {
      const r = await filmApi.getPlanJobs(planId).catch(() => null);
      const running = (r?.jobs || []).some((j) => j.type === type);
      if (running) sawRunning = true;
      else if (sawRunning || Date.now() - t0 > 8000) {
        const failed = (r?.failures || []).find((f) => f.type === type && f.minutesAgo <= 3);
        return failed ? 'failed' : 'done';
      }
      await new Promise((r2) => setTimeout(r2, 3000));
    }
    return 'timeout';
  };

  const runPass = async (key: string, run: () => Promise<any>) => {
    if (!ensureClean(`running pass ${key}`)) return;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await run();
      message.info(`${key}: đang chạy ở nền — đừng bấm lại, kết quả tự hiện khi xong.`);
      await loadJobs();
      setTimeout(reload, 3000);
    } catch (e: any) {
      message.error(apiErrorText(e, `Pass ${key}`));
      await Promise.all([loadJobs(), reload()]);
    } finally { setBusy((b) => ({ ...b, [key]: false })); }
  };

  // So op với giá trị SERVER — field nào bằng thì BỎ khỏi op (sửa về như cũ = hết "đang sửa"). dirty suy từ ops thực.
  const eqJson = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const trimEventOp = (e: EditState, id: string) => {
    const op = e.ops.ev[id]; const sv = data ? findEvent(data, id) : undefined; if (!op || !sv) return;
    const c: any = {};
    if ('action' in op && !eqJson(op.action, sv.action)) c.action = op.action;
    if ('kind' in op && !eqJson(op.kind, sv.kind)) c.kind = op.kind;
    if ('genDurationS' in op && !eqJson(op.genDurationS, sv.genDurationS)) c.genDurationS = op.genDurationS;
    if ('beatMeta' in op && !eqJson(op.beatMeta, sv.beatMeta)) c.beatMeta = op.beatMeta;
    if (Object.keys(c).length) e.ops.ev[id] = c; else delete e.ops.ev[id];
  };
  const trimPromptOp = (e: EditState, id: string) => {
    if (!(id in e.ops.prompt)) return;
    const sv = data ? findEvent(data, id) : undefined; if (!sv) return;
    if (eqJson(e.ops.prompt[id], sv.authorPrompt)) delete e.ops.prompt[id];
  };
  const trimDlgOp = (e: EditState, id: string) => {
    if (!(id in e.ops.dlg)) return;
    const sv = data ? findEvent(data, id) : undefined; if (!sv) return;
    const norm = (arr: any[]) => (arr || []).map((d) => ({ speakerName: d.speakerName ?? null, textEn: d.textEn ?? null, dialogueMode: d.dialogueMode, afterActionBeatOrder: d.afterActionBeatOrder ?? null }));
    if (eqJson(norm(e.ops.dlg[id]), norm(sv.dialogueLines || []))) delete e.ops.dlg[id];
  };
  const trimShotOp = (e: EditState, id: string) => {
    const op = e.ops.sh[id]; if (!op) return;
    const sv: any = data ? findShot(data, id)?.shot : undefined; if (!sv) return;
    const c: Record<string, unknown> = {};
    for (const k of Object.keys(op)) { if (!eqJson(op[k], sv[k])) c[k] = op[k]; }
    if (Object.keys(c).length) e.ops.sh[id] = c; else delete e.ops.sh[id];
  };

  // ── Field edits → ghi vào NHÁP (thay auto-save-per-field cũ). KHÔNG gọi API; commit ở nút 💾 Lưu. ──
  const saveEvent = (id: string, d: any) => mutate((e) => {
    const ev = findEvent(e.draft, id); if (ev) Object.assign(ev, d);
    e.ops.ev[id] = { ...(e.ops.ev[id] || {}), ...d };
    trimEventOp(e, id);
  });
  const saveDialogue = (id: string, lines: DlgLite[]) => mutate((e) => {
    const ev = findEvent(e.draft, id);
    if (ev) ev.dialogueLines = lines.map((l, i) => ({ id: tmpLineId(), orderIndex: i + 1, speakerName: l.speakerName ?? null, textEn: l.textEn ?? null, dialogueMode: l.dialogueMode, afterActionBeatOrder: l.afterActionBeatOrder ?? null }));
    e.ops.dlg[id] = lines;
    trimDlgOp(e, id);
  });
  const saveShot = (id: string, d: Record<string, unknown>) => mutate((e) => {
    const found = findShot(e.draft, id); if (found) Object.assign(found.shot, d);
    e.ops.sh[id] = { ...(e.ops.sh[id] || {}), ...d };
    trimShotOp(e, id);
  });
  // Sửa nhân vật của event = sửa `presence.visible` các shot trong MỘT lần mutate (gọi saveShot liên tiếp sẽ mất op).
  // Bỏ tên → gỡ khỏi mọi shot; thêm tên chưa có → gắn vào shot đầu của event.
  const applyEventCast = (e: EditState, ev: FilmEvent, names: string[]) => {
    const keep = new Set(names.map((n) => n.toLowerCase()));
    const shots = ev.shots || [];
    const present = new Set<string>();
    const touched = new Set<string>();
    for (const sh of shots) {
      const p: any = sh.presence || {};
      const vis: string[] = (p.visible || []).filter((n: string) => keep.has(n.toLowerCase()));
      vis.forEach((n) => present.add(n.toLowerCase()));
      if (vis.length !== (p.visible || []).length) { sh.presence = { ...p, visible: vis } as any; touched.add(sh.id); }
    }
    const add = names.filter((n) => !present.has(n.toLowerCase()));
    if (add.length && shots[0]) {
      const p: any = shots[0].presence || {};
      shots[0].presence = { ...p, visible: [...(p.visible || []), ...add] } as any; touched.add(shots[0].id);
    }
    for (const id of Array.from(touched)) {
      const sh = shots.find((x) => x.id === id)!;
      e.ops.sh[id] = { ...(e.ops.sh[id] || {}), presence: sh.presence };
      trimShotOp(e, id);
    }
  };
  const setEventCast = (eventId: string, names: string[]) => mutate((e) => {
    const ev = findEvent(e.draft, eventId); if (ev) applyEventCast(e, ev, names);
  });
  // Scene không có danh sách riêng — dàn nhân vật của cảnh là hợp các event ⇒ sửa ở scene = áp cho mọi event trong cảnh.
  const setSceneCast = (sceneId: string, names: string[]) => mutate((e) => {
    const sc = e.draft.scenes.find((x) => x.id === sceneId); if (!sc) return;
    const before = new Set(sc.events.flatMap((ev) => (ev.shots || []).flatMap((s) => s.presence?.visible || [])).map((n) => n.toLowerCase()));
    const removed = Array.from(before).filter((n) => !names.some((x) => x.toLowerCase() === n));
    const added = names.filter((n) => !before.has(n.toLowerCase()));
    for (const ev of sc.events) {
      const cur = Array.from(new Set((ev.shots || []).flatMap((s) => s.presence?.visible || [])));
      const next = [...cur.filter((n) => !removed.includes(n.toLowerCase())), ...added];
      if (next.length !== cur.length || added.length) applyEventCast(e, ev, next);
    }
  });
  const savePrompt = (eventId: string, prompt: string) => mutate((e) => {
    const ev = findEvent(e.draft, eventId); if (ev) { ev.authorPrompt = prompt; ev.authorStatus = 'EDITED'; }
    e.ops.prompt[eventId] = prompt;
    trimPromptOp(e, eventId);
  });
  // Epic 3: áp NHIỀU op đề xuất của chatbot vào nháp trong MỘT lần mutate (gọi save* liên tiếp sẽ đọc `edit` cũ → mất op).
  const applyChatOps = (ops: BatchEditOp[]): number => {
    let applied = 0;
    const rejected: string[] = []; // 260923: op cấu trúc bot đề xuất mà trái luật event dạng chuỗi
    mutate((e) => {
      for (const op of ops) {
        if (op.type === 'updateEvent' && (op.action !== undefined || op.kind !== undefined)) {
          const ev = findEvent(e.draft, op.eventId); if (!ev) continue;
          const d: any = {};
          if (op.action !== undefined) { ev.action = op.action ?? null; d.action = op.action; }
          if (op.kind !== undefined) { ev.kind = op.kind as any; d.kind = op.kind; }
          e.ops.ev[op.eventId] = { ...(e.ops.ev[op.eventId] || {}), ...d };
          trimEventOp(e, op.eventId); applied++;
        } else if (op.type === 'updateShot') {
          const found = findShot(e.draft, op.shotId); if (!found) continue;
          const d: Record<string, unknown> = {};
          for (const k of ['shotSize', 'angle', 'cameraMove', 'framing', 'effect', 'purpose', 'frameNote'] as const) if ((op as any)[k] !== undefined) d[k] = (op as any)[k]; // framing/effect: Epic2-camera, chatbot đè được (260722)
          if (!Object.keys(d).length) continue;
          Object.assign(found.shot, d);
          e.ops.sh[op.shotId] = { ...(e.ops.sh[op.shotId] || {}), ...d };
          trimShotOp(e, op.shotId); applied++;
        } else if (op.type === 'replaceDialogue') {
          const ev = findEvent(e.draft, op.eventId); if (!ev) continue;
          const lines: DlgLite[] = op.lines.map((l) => ({ speakerName: l.speakerName ?? null, textEn: l.textEn ?? null, dialogueMode: l.dialogueMode }));
          ev.dialogueLines = lines.map((l, i) => ({ id: tmpLineId(), orderIndex: i + 1, speakerName: l.speakerName ?? null, textEn: l.textEn ?? null, dialogueMode: l.dialogueMode }));
          e.ops.dlg[op.eventId] = lines;
          trimDlgOp(e, op.eventId); applied++;
        } else if (op.type === 'updatePrompt' && op.authorPrompt !== undefined) {
          const ev = findEvent(e.draft, op.eventId); if (!ev) continue;
          ev.authorPrompt = op.authorPrompt ?? null; ev.authorStatus = 'EDITED';
          e.ops.prompt[op.eventId] = op.authorPrompt ?? null;
          trimPromptOp(e, op.eventId); applied++;
        } else if (STRUCT_TYPES.has(op.type)) {
          // E4-P3: op CẤU TRÚC bot đề xuất — vào nháp y như user tự bấm nút HITL (tempId do FE sinh).
          const st = { ...(op as any), ...((op as any).type === 'splitEvent' || (op as any).type === 'insertAfter' ? { tempId: tmpEventId() } : {}) } as StructOp;
          const why = structRejectReason(e.draft, st);
          if (why) { rejected.push(why); continue; }
          const before = e.draft;
          e.draft = applyStructToDraft(e.draft, st);
          if (e.draft === before) continue; // op không hợp lệ trên draft (event mất, gộp cuối scene…)
          e.ops.struct.push(st);
          pruneOrphanFieldOps(e.draft, e.ops);
          applied++;
        }
      }
    });
    if (rejected.length) message.warning(`Bỏ qua ${rejected.length} thao tác cấu trúc: ${Array.from(new Set(rejected)).join(' · ')}`);
    return applied;
  };

  // ── Lưu 1 lượt / Undo / Huỷ nháp ──
  const STALE_HINT = 'Duration/continuity/prompt cleared — rerun ⏱️ Duration + 🔗 Continuity + 📝 Prompt before generating';
  const doSave = async () => {
    if (!edit) return;
    const ops = buildBatchOps(edit.ops);
    if (!ops.length) return;
    setBusy((b) => ({ ...b, save: true }));
    try {
      const hadStruct = edit.ops.struct.length > 0; // E4-P2: cấu trúc đổi → duration/ledger/prompt bị xoá server-side
      const { plan } = await filmApi.batchEditPlan(planId, ops);
      applyServerPlan(plan);
      message.success(`Saved ${ops.length} change${ops.length > 1 ? 's' : ''}`);
      if (hadStruct) message.warning(STALE_HINT, 6);
      // Epic 2 Phase 2: nếu bật auto-sync và có event prompt cũ → tự re-author ngay (nền, tự poll).
      const needSync = plan.scenes.flatMap((s) => s.events).some((e) => { const st = e.staleStages || []; return st.includes('prompt') || st.includes('continuity'); });
      if (autoSync && needSync) syncPrompts(plan);
    } catch (e: any) { message.error(serverMessageOf(e) || e?.message); } // 260923 D2: ConflictError trả { messages: [] }
    finally { setBusy((b) => ({ ...b, save: false })); }
  };
  const undo = () => setHistory((h) => { if (!h.length) return h; setEdit(h[h.length - 1]); return h.slice(0, -1); });
  const discardDraft = () => { if (data) { applyServerPlan(data); message.info('Draft discarded'); } };

  const deleteShot = async (id: string) => { if (!ensureClean('deleting a shot')) return; try { await filmApi.deleteEventShot(id); message.success('Shot deleted'); reload(); } catch (e: any) { message.error(e?.response?.data?.message || e?.message); } };
  const addShot = async (eventId: string) => { if (!ensureClean('adding a shot')) return; try { await filmApi.addEventShot(eventId); message.success('Shot added'); reload(); } catch (e: any) { message.error(e?.response?.data?.message || e?.message); } };
  // ── HITL CRUD event (Epic 5): sửa ranh giới AI chia (judge/linter chỉ ra, đây là chỗ sửa) ──
  // Sau tách/gộp, duration + ledger + prompt bị XOÁ (stale) → phải chạy lại pass trước khi gen.
  // E4-P2: 4 thao tác CẤU TRÚC nay ghi vào NHÁP (không gọi API ngay) → gom chung 1 lượt 💾 Lưu với
  // sửa chữ, Undo được. Vì thế KHÔNG còn ensureClean chặn "Save/Discard before splitting…".
  const pushStruct = (op: StructOp, hint: string) => {
    // 260923: luật event dạng chuỗi (hook đứng riêng · không gộp v1+v2 · chưa tách v2) — báo lý do, KHÔNG đổi nháp.
    const why = edit ? structRejectReason(edit.draft, op) : null;
    if (why) { message.warning(why); return; }
    mutate((e) => {
      e.draft = applyStructToDraft(e.draft, op);
      e.ops.struct.push(op);
      pruneOrphanFieldOps(e.draft, e.ops); // op field trỏ event/shot vừa biến mất → bỏ (tránh lỗi lúc Lưu)
    });
    message.info(`${hint} — đã ghi vào nháp, bấm 💾 Lưu để áp dụng`);
  };
  // E1-P2 (R8): v1 `{keepShotCount}` · v2 `{keepBeatCount}` — nháp + structRejectReason chặn sai loại/k sai (cùng luật server).
  const splitEvent = (eventId: string, opt: { keepShotCount?: number; keepBeatCount?: number }) =>
    pushStruct({ type: 'splitEvent', eventId, ...opt, tempId: tmpEventId() }, 'Tách event');
  const mergeEventNext = (eventId: string) =>
    pushStruct({ type: 'mergeNext', eventId }, 'Gộp với event kế tiếp');
  const insertEventAfter = (eventId: string) =>
    pushStruct({ type: 'insertAfter', eventId, tempId: tmpEventId() }, 'Chèn event trống');
  const deleteEvent = (eventId: string) =>
    pushStruct({ type: 'deleteEvent', eventId }, 'Xoá event');

  const moveShot = async (ev: FilmEvent, id: string, dir: -1 | 1) => {
    if (!ensureClean('reordering shots')) return;
    const ids = ev.shots.map((s) => s.id);
    const i = ids.indexOf(id); const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try { await filmApi.reorderEventShots(ev.id, ids); reload(); } catch (e: any) { message.error(e?.response?.data?.message || e?.message); }
  };

  const genEvent = async (eventId: string, variant?: boolean) => {
    if (layoutFour) { message.warning(LAYOUT_FOUR_GEN_HINT); return; }
    if (!ensureClean('generating a clip')) return;   // gen đọc prompt/duration từ SERVER → phải Lưu nháp trước
    setBusy((b) => ({ ...b, [`gen-${eventId}`]: true })); // nút "generating" bật NGAY (feedback tức thì)
    try {
      // Video của plan opt-in: gửi ĐÚNG cấu hình đã lưu (BE 409 nếu lệch) + số bản theo lượt; video legacy: ⚙️ như cũ.
      const evNow = data?.scenes.flatMap((s) => s.events).find((e) => e.id === eventId);
      const t = legacyGenTuningOf(evNow, tuningOf(eventId));
      await filmApi.genEvent(eventId, variant, t);
      message.info(`Đang gen ${t.count && t.count > 1 ? `${t.count} bản` : 'clip'} (${t.model || (optInTuningOf(evNow) ? '2.5' : 'mini')} · ${t.resolution || '480p'}) ở nền…`);
      // Bug3 fix: POLL MỒI — reload tới khi thấy clip PENDING của event này (clip ghi DB có độ trễ; 1 lần
      // setTimeout dễ trượt → user tưởng chưa gen). Giữ nút busy suốt window; sau đó poll chung (useEffect
      // line ~586) tiếp quản PENDING→DONE. Gen đã ensureClean nên reload không wipe nháp.
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const fresh = await filmApi.getEventPlan(planId);
        applyServerPlan(fresh);
        const ev = fresh.scenes.flatMap((s) => s.events).find((e) => e.id === eventId);
        if ((ev?.clips || []).length) break; // đã có clip (PENDING/DONE) hiện trên UI → dừng mồi
      }
    }
    // 260923 Epic2-P1 (N3) — 409 cửa mode (lệch tab: đổi mode ở tab khác trong lúc tab này còn bản cũ) đọc
    // qua `apiErrorText` (đọc được cả `message` lẫn `messages[0]`), không đọc thẳng `data.message`.
    catch (e: any) { message.error(apiErrorText(e, 'Gen clip')); }
    finally { setBusy((b) => ({ ...b, [`gen-${eventId}`]: false })); }
  };
  const selectClip = async (eventId: string, clipId: string) => { if (!ensureClean('selecting a candidate')) return; try { await filmApi.selectEventClip(eventId, clipId); message.success('Candidate selected'); reload(); } catch (e: any) { message.error(e?.response?.data?.message || e?.message); } };
  // 260923 Epic2-P1 Bước 8 — đè mode riêng của MỘT event (null = theo plan). Route đồng bộ, không đụng authorPrompt.
  // 260923 Epic2-P3 (M2) — thành công ⇒ nếu event giờ rơi vào MISMATCH + canAuthor + không EDITED, tự dựng
  // lại NGAY (1 lượt AI, không hỏi — một thao tác chọn tay cho đúng MỘT event). `setEventPromptMode` chỉ trả
  // 4 trường (không đủ để tính lại) ⇒ theo tiền lệ `genEvent`: nạp lại plan rồi tự tìm đúng event.
  const setEventPromptModeH = async (eventId: string, mode: FilmPromptMode | null) => {
    if (!ensureClean('đổi mode prompt của event')) return;
    try {
      await filmApi.setEventPromptMode(eventId, mode);
      message.success('Đã đổi mode prompt');
      const fresh = await filmApi.getEventPlan(planId);
      applyServerPlan(fresh);
      const ev = fresh.scenes.flatMap((s) => s.events).find((e) => e.id === eventId);
      if (ev) {
        const effectiveMode = resolvePromptMode(ev.promptMode, fresh.plan.promptMode);
        const spokenLineCount = (ev.dialogueLines || []).filter((d) => d.textEn && String(d.dialogueMode || '').toUpperCase() !== 'NONE').length;
        const shouldRebuild = shouldRebuildMismatchOf({
          effectiveMode, authorPrompt: ev.authorPrompt, authorPromptMode: ev.authorPromptMode, authorStatus: ev.authorStatus,
          beatMeta: ev.beatMeta, shotCount: (ev.shots || []).length, spokenLineCount,
        });
        if (shouldRebuild) {
          message.loading({ content: `Đang dựng lại prompt E${ev.eventOrder} theo mode mới`, key: 'rebuild-one', duration: 0 });
          try {
            await filmApi.authorOneEventPrompt(eventId); // KHÔNG truyền force (M13 mặc định false)
            message.success({ content: `Đã dựng lại prompt E${ev.eventOrder}`, key: 'rebuild-one' });
          } catch (e2: any) { message.error({ content: apiErrorText(e2, `Dựng lại prompt E${ev.eventOrder}`), key: 'rebuild-one' }); }
          reload();
        }
      }
    } catch (e: any) { message.error(apiErrorText(e, 'Đổi mode prompt')); }
  };
  // 260923 Epic2-P1 Bước 8 — "Đổi kiểu prompt" CẢ PLAN: bấm ⇒ ensureClean ⇒ dryRun (xem-trước-ảnh-hưởng) ⇒
  // modal → OK mới gọi thật (ghi + ghim prompt sửa tay trong một transaction ở server) ⇒ reload.
  const openChangePromptModeModal = async () => {
    if (!data) return;
    if (!ensureClean('đổi kiểu prompt cả plan')) return;
    const target: FilmPromptMode = data.plan.promptMode === 'NATURAL' ? 'TECHNICAL' : 'NATURAL';
    setModeModal({ target, loading: true, rebuild: true });
    try {
      const preview = await filmApi.setPlanPromptMode(planId, target, true);
      setModeModal({ target, loading: false, rebuild: true, preview });
    } catch (e: any) { message.error(apiErrorText(e, 'Xem trước đổi mode')); setModeModal(null); }
  };
  // 260923 Epic2-P3 (U4/Bước 3) — OK: đổi mode xong; nếu ô "Dựng lại ngay" bật và N>0 ⇒ gọi
  // `authorEventPrompts({rebuildMismatch:true})` (nền). Lỗi dựng lại KHÔNG làm hỏng việc đổi mode (đã đổi
  // xong) — báo riêng bằng `apiErrorText`.
  const confirmChangePromptMode = async () => {
    if (!modeModal || !data) return;
    const { target, rebuild, preview } = modeModal;
    const allEvents = data.scenes.flatMap((s) => s.events);
    const n = preview ? countRebuildableAfterModeSwitch(preview.mismatchAfter, allEvents, target) : 0;
    try {
      await filmApi.setPlanPromptMode(planId, target, false);
      message.success(`Đã đổi plan sang mode ${MODE_LABEL_VI[target]}`);
      setModeModal(null);
      if (rebuild && n > 0) {
        try {
          const r = await filmApi.authorEventPrompts(planId, { rebuildMismatch: true });
          message.info(r?.message || `Đang dựng lại ${n} prompt lệch mode (nền)`);
          // 260923 Epic2-P3 (R1) — `r.warning` (câu "N prompt sửa tay lệch mode không được dựng lại…" của
          // Bước 2 + cảnh báo thiếu plate) từng bị bỏ ⇒ không bao giờ tới người dùng. Hiện thêm, vẫn giữ
          // `r.message` (cùng cách `authorPrompts` đang làm).
          if (r?.warning) message.warning({ content: r.warning, duration: 8 });
          await loadJobs();
        } catch (e2: any) { message.error(apiErrorText(e2, 'Dựng lại prompt lệch mode')); }
      }
      reload();
    } catch (e: any) { message.error(apiErrorText(e, 'Đổi mode prompt')); }
  };
  // 260923 Epic2-P3 (M3) — nút thẻ "Dựng lại prompt": `force` quyết định bởi `rebuildCallOf` (EDITED ⇒ true,
  // sau Popconfirm; còn lại ⇒ undefined, KHÔNG ghi đè EDITED nếu FE lỡ tính nhầm — M13 mặc định false).
  const rebuildEventPromptH = async (eventId: string, force?: boolean) => {
    setBusy((b) => ({ ...b, [`rebuild-${eventId}`]: true }));
    try { await filmApi.authorOneEventPrompt(eventId, { force }); message.success('Đã dựng lại prompt'); reload(); }
    catch (e: any) { message.error(apiErrorText(e, 'Dựng lại prompt')); }
    finally { setBusy((b) => ({ ...b, [`rebuild-${eventId}`]: false })); }
  };
  // 260923 Epic2-P3 (Bước 3) — nút bước Prompts "Dựng lại prompt lệch mode (N)" (PipelineStepper).
  const rebuildMismatchPrompts = async () => {
    if (!ensureClean('dựng lại prompt lệch mode')) return;
    setBusy((b) => ({ ...b, rebuildMismatch: true }));
    try {
      const r = await filmApi.authorEventPrompts(planId, { rebuildMismatch: true });
      message.info(r?.message || 'Đang dựng lại prompt lệch mode (nền)');
      // 260923 Epic2-P3 (R1) — hiện thêm `r.warning` (cùng cách `authorPrompts`), vẫn giữ `r.message` ở trên.
      if (r?.warning) message.warning({ content: r.warning, duration: 8 });
      await loadJobs();
      setTimeout(reload, 2000);
    } catch (e: any) { message.error(apiErrorText(e, 'Dựng lại prompt lệch mode')); await loadJobs(); }
    finally { setBusy((b) => ({ ...b, rebuildMismatch: false })); }
  };
  // 260927 mục N5 — nút bước Prompts "Dựng lại prompt lệch bộ đồ (N)". Phạm vi TƯỜNG MINH
  // (`data.plan.lookMismatchEventIds`, BE tính lúc đọc) — không đụng `rebuildMismatch` (mode).
  const rebuildLookMismatchPrompts = async () => {
    if (!ensureClean('dựng lại prompt lệch bộ đồ')) return;
    const ids = (data as any)?.plan?.lookMismatchEventIds || [];
    if (!ids.length) return;
    setBusy((b) => ({ ...b, rebuildLookMismatch: true }));
    try {
      const r = await filmApi.authorEventPrompts(planId, { rebuildEventIds: ids });
      message.info(r?.message || 'Đang dựng lại prompt lệch bộ đồ (nền)');
      if (r?.warning) message.warning({ content: r.warning, duration: 8 });
      await loadJobs();
      setTimeout(reload, 2000);
    } catch (e: any) { message.error(apiErrorText(e, 'Dựng lại prompt lệch bộ đồ')); await loadJobs(); }
    finally { setBusy((b) => ({ ...b, rebuildLookMismatch: false })); }
  };
  const deleteClip = async (clipId: string) => { if (!ensureClean('deleting a variant')) return; try { await filmApi.deleteEventClip(clipId); message.success('Variant deleted'); reload(); } catch (e: any) { message.error(e?.response?.data?.message || e?.message); } };
  // `warning` (thiếu plate bối cảnh) KHÔNG chặn job — hiện lâu hơn info để người dùng kịp đọc mà quyết gen ảnh nền.
  const authorPrompts = async () => { if (!ensureClean('authoring prompts')) return; setBusy((b) => ({ ...b, author: true })); try { const r = await filmApi.authorEventPrompts(planId); if (r?.warning) message.warning({ content: r.warning, duration: 8 }); else message.info('Building prompts (background)…'); await loadJobs(); setTimeout(reload, 2000); } catch (e: any) { message.error(apiErrorText(e, 'Dựng prompt')); await loadJobs(); } finally { setBusy((b) => ({ ...b, author: false })); } };
  // Epic 2: re-author prompt cho các event out-of-date (prompt-stale) — scoped từ event stale đầu tiên.
  const syncPrompts = async (fromPlan?: EventPlan) => {
    if (!ensureClean('syncing')) return;
    const src = fromPlan || data;
    const events = (src?.scenes || []).flatMap((s) => s.events);
    const continuityStale = events.some((e) => (e.staleStages || []).includes('continuity'));
    const promptStales = events.filter((e) => (e.staleStages || []).includes('prompt'));
    if (!continuityStale && !promptStales.length) return;
    const targetIds = new Set(promptStales.map((e) => e.id));
    setBusy((b) => ({ ...b, sync: true }));
    message.loading({ content: continuityStale ? 'Rebuilding continuity, then re-authoring prompts (AI)…' : 'Re-authoring out-of-date prompts (AI, background)…', key: 'sync', duration: 0 });
    try {
      // Continuity chain: sửa 1 event làm ledger event sau lệch → dựng lại cả chuỗi TRƯỚC khi author.
      // 🔴 260806: pass này nay chạy NỀN (202) nên phải CHỜ job xong — author trên ledger cũ là sai âm thầm.
      if (continuityStale) {
        await filmApi.checkContinuity(planId);
        const r = await waitForPlanJob('FILM_CONTINUITY');
        if (r === 'failed') {
          message.error({ content: 'Dựng lại continuity thất bại — xem lý do ở băng đỏ phía trên. Chưa author prompt để tránh dùng ledger cũ.', key: 'sync' });
          await Promise.all([loadJobs(), reload()]);
          setBusy((b) => ({ ...b, sync: false }));
          return;
        }
        if (r === 'timeout') message.warning('Continuity chạy lâu bất thường — vẫn tiếp tục author, kiểm lại sau.');
      }
      if (promptStales.length) {
        const minOrder = Math.min(...promptStales.map((e) => e.eventOrder));
        await filmApi.authorEventPrompts(planId, { fromEventOrder: minOrder }); // nền 202
        // Poll tới khi các event đích hết prompt-stale (author là LLM, mất vài chục giây).
        let done = false;
        for (let i = 0; i < 40 && !done; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          const p = await filmApi.getEventPlan(planId);
          const remain = p.scenes.flatMap((s) => s.events).filter((e) => targetIds.has(e.id) && (e.staleStages || []).includes('prompt'));
          if (!remain.length) { applyServerPlan(p); message.success({ content: 'Synced — now regenerate their clips to refresh.', key: 'sync' }); done = true; }
        }
        if (!done) { applyServerPlan(await filmApi.getEventPlan(planId)); message.warning({ content: 'Still authoring — reload in a moment.', key: 'sync' }); }
      } else {
        // Chỉ continuity: fetch lại để phản ánh cờ continuity đã clear.
        applyServerPlan(await filmApi.getEventPlan(planId));
        message.success({ content: 'Continuity rebuilt — regenerate affected clips to refresh.', key: 'sync' });
      }
    } catch (e: any) { message.error({ content: apiErrorText(e, 'Sync prompt'), key: 'sync' }); }
    finally { setBusy((b) => ({ ...b, sync: false })); }
  };
  const genAll = async () => {
    if (layoutFour) { message.warning(LAYOUT_FOUR_GEN_HINT); return; }
    if (!ensureClean('generating all')) return;
    setBusy((b) => ({ ...b, genall: true }));
    try {
      // Gửi đúng cấu hình ⚙️ người dùng đã đặt ở từng clip — clip không đụng tới thì server chạy mặc định.
      // Video của plan opt-in KHÔNG nằm trong perEvent: BE dùng cấu hình đã lưu của chúng.
      const perEvent = Object.fromEntries(
        Object.entries(batchOverridesOf(tuningByEvent, data?.scenes.flatMap((s) => s.events) ?? []))
          .filter(([, t]) => t.model || t.resolution)
          .map(([id, t]) => [id, { model: t.model, resolution: t.resolution }]),
      );
      await filmApi.genEvents(planId, perEvent);
      const n = Object.keys(perEvent).length;
      message.info(`Generating all events (background)${n ? ` — ${n} clip(s) use their own ⚙️ setting` : ''}…`);
      // Bug3/4: poll mồi cho tới khi thấy clip PENDING (như genEvent) → nút "generating" giữ feedback,
      // rồi poll chung tiếp quản. Đừng để 1 reload trượt race làm user tưởng chưa gen.
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const fresh = await filmApi.getEventPlan(planId);
        applyServerPlan(fresh);
        if (fresh.scenes.some((s) => s.events.some((e) => (e.clips || []).some((c) => c.status === 'PENDING')))) break;
      }
    } catch (e: any) { message.error(apiErrorText(e, 'Gen clip cả plan')); await loadJobs(); }
    finally { setBusy((b) => ({ ...b, genall: false })); }
  };
  const runPassByKey = (key: string) => { const p = PASSES.find((x) => x.key === key); if (p) runPass(key, () => p.run(planId)); };

  /**
   * "Chạy hết các bước còn lại".
   *
   * 🔴 260806: phần KỊCH BẢN nay đi bằng MỘT việc nền (`runScriptPasses`) thay vì FE await 5 pass tuần tự.
   * Chuỗi 5 pass chắc chắn vượt trần 180 giây của gateway (đo thật) nên cách cũ luôn đứt giữa đường: dữ liệu
   * ghi được một phần, UI báo lỗi, không ai biết đã tới bước nào. Nay bấm một lần, thứ tự pass do server giữ.
   *
   * 🔴 260807: ảnh nền + outfit-ref nay chạy LUÔN ở cuối `runScriptPasses` (server). Nhánh kick chúng dưới đây
   * vẫn giữ vì nó là đường sửa chữa: plan cũ đã qua bước kịch bản, hoặc lượt trước gen ref lỗi lẻ tẻ. Cả hai
   * đều idempotent nên chạy lại không tốn thêm lượt gen nào.
   */
  const runRemaining = async () => {
    if (!data) return;
    if (layoutFour) { message.warning(LAYOUT_FOUR_GEN_HINT); return; }
    if (!ensureClean('running the remaining steps')) return;
    setBusy((b) => ({ ...b, remaining: true }));
    try {
      const s0 = computeStatus(data);
      const scriptMissing = !s0.hasDialogue || !s0.hasShots || !s0.hasContinuity || !s0.hasDurations;
      if (scriptMissing) {
        await filmApi.runScriptPasses(planId);
        message.info('Running in background (~3–8 min): dialogue → locations → camera → continuity → durations → plates + outfits. It refreshes itself; hit this button again to move on to scene outfits.', 8);
        await loadJobs();
        setTimeout(reload, 3000);
        return;
      }
      const s = s0;
      /**
       * 260927 mục K — plan đã xong kịch bản mà phần References còn thiếu (cảnh chưa chia bộ đồ · bối cảnh
       * thiếu ảnh · (BytePlus) trang phục thiếu ảnh ma-nơ-canh) thì trước đây KHÔNG có nút nào kích lại chuỗi
       * mục E (Leader phải gọi thẳng API `POST /plans/:id/script-passes`). Nay gọi ĐÚNG MỘT lượt
       * `runScriptPasses` (không `force`, chạy nền, idempotent) thay 3 lượt lẻ (`proposeSceneLooks` /
       * `genLocationPlates` / `prepareOutfits`) — chuỗi đó ĐÃ làm cả 4 việc theo thứ tự (bộ đồ từ cast → chia
       * bộ đồ theo cảnh → ảnh nền bối cảnh → đạo cụ vào Bank), nên cùng lượt này cũng lấp cho nhân vật
       * (ROLE mới promote, mục H) chưa có hồ sơ/bộ đồ và đạo cụ chưa vào Bank — hai việc FE không tự biết
       * riêng để kích đúng lúc.
       */
      if (referencesIncomplete(s, sceneLooks.length)) {
        await filmApi.runScriptPasses(planId);
        message.info('Script done ✓ — running the references step in background (~1–3 min): outfits from cast → scene outfit assignment → location backgrounds → props into Bank. It refreshes itself; hit this button again once done.', 8);
        await loadJobs();
        setTimeout(reload, 3000);
        return;
      }
      if (s.refsBlocked) {
        message.warning({
          content: data.plan.engine === 'BYTEPLUS'
            ? `Missing DC: ${s.dcMissing.join(', ')} — assign manually in "Manage refs" then click "Author prompt" → "Gen all".`
            : `Missing worn photo: ${s.wornMissing.join(', ')} — open "Custom Prompt" on their card in the Character Bank, then click "Author prompt" → "Gen all".`,
          key: 'rem', duration: 8,
        });
      } else if (!s.authorDone) {
        // 260923 Epic2-P1 (L12, B2) — CHỈ dựng những event mode Kỹ thuật CÒN THIẾU chữ (`onlyMissing`); event
        // bị chặn (MISMATCH/Tự nhiên MISSING) không nằm trong phạm vi này — author bỏ qua chúng mãi mãi
        // nếu gọi lại full pass (bug lặp vô hạn đã đóng — rr0 B2).
        await filmApi.authorEventPrompts(planId, { onlyMissing: true }); message.success({ content: 'Refs ready → authoring remaining prompts (background). When done, click "Gen all".', key: 'rem', duration: 6 });
      } else if (s.blockedOrders.length > 0 && s.nGenReady === 0) {
        // 260923 Epic2-P1 — author hết việc (authorDone) nhưng KHÔNG còn event gen được: mọi event kẹt ở
        // MISMATCH/MISSING — nói rõ danh sách + cách gỡ, KHÔNG gọi gen-events (0 event sẽ chạy).
        // 260923 Epic2-P2 (Bước 7) — tách 2 nhóm cách gỡ khác nhau: lệch mode (câu cũ) vs chưa dựng được
        // (event đời cũ không nhịp / trống / chưa shot — câu mới, N10).
        const { mismatchMsg, missingMsg } = runRemainingBlockedMessages(s.mismatchOrders, s.missingBlockedOrders);
        message.warning({
          content: `Không còn gì tự động dựng được — ${[mismatchMsg, missingMsg].filter(Boolean).join(' ')}`,
          key: 'rem', duration: 10,
        });
      } else if (!s.genDone) {
        await filmApi.genEvents(planId); message.success({ content: 'Started generating all clips (background).', key: 'rem' });
      } else message.success({ content: 'All steps done.', key: 'rem' });
      await loadJobs();
      setTimeout(reload, 2500);
    } catch (e: any) {
      message.error(apiErrorText(e, 'Run remaining steps'));
      await Promise.all([loadJobs(), reload()]);
    } finally { setBusy((b) => ({ ...b, remaining: false })); }
  };
  const toggleLock = async (sceneId: string, locked: boolean) => { if (!ensureClean('locking/unlocking a scene')) return; try { await filmApi.setSceneLock(sceneId, locked); reload(); } catch (e: any) { message.error(e?.response?.data?.message || e?.message); } };

  /**
   * Đè thời điểm trong ngày của cảnh (260811).
   *
   * Prompt đã author mang `[Time]` cũ trong chuỗi nên KHÔNG tự đổi theo — server đánh dấu stale và trả về
   * số event bị đánh dấu; báo rõ cho admin biết phải author lại, chứ đừng để họ tưởng đổi xong là xong.
   */
  const setSceneTime = async (sceneId: string, timeOfDay: string | null) => {
    if (!ensureClean('changing the time of day')) return;
    try {
      const r = await filmApi.setSceneTimeOfDay(sceneId, timeOfDay);
      message.success(r.staleEvents
        ? `Time of day updated — ${r.staleEvents} event${r.staleEvents > 1 ? 's' : ''} need re-authoring before generating`
        : 'Time of day updated');
      reload();
    } catch (e: any) { message.error(apiErrorText(e, 'Set time of day')); }
  };

  const runVerify = async () => {
    setBusy((b) => ({ ...b, verify: true }));
    message.loading({ content: 'Grading plan (L1 linter + L2 judge)…', key: 'verify', duration: 0 });
    try {
      const [lint, judge] = await Promise.all([filmApi.lintPlan(planId), filmApi.judgePlan(planId)]);
      setVerify({ lint, judge });
      message.success({ content: `Graded — overall ${judge.overall}/5, ${lint.counts.error} hard errors`, key: 'verify' });
    } catch (e: any) { message.error({ content: apiErrorText(e, 'Chấm plan'), key: 'verify' }); }
    finally { setBusy((b) => ({ ...b, verify: false })); }
  };

  if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />;
  if (!data || !edit) return <Empty description="Could not load event plan" />;

  const storyboardCtx: StoryboardCtx | undefined = isSkyPlan(data.plan.flowVersion)
    ? { byEvent: sbByEvent, loaded: sbState.loaded, loading: sbState.loading, loadError: sbState.error, pos: sbPos, jobs, failures, onReload: reloadStoryboards, onJobsRefresh: loadJobs }
    : undefined;
  const view = edit.draft;   // render từ NHÁP (chứa cả sửa chưa lưu). Stepper/Export dùng bản SERVER (data).
  const downgradeChildren = downgradeChildIds
    ? data.scenes.flatMap((s) => s.events).filter((e) => downgradeChildIds.includes(e.id))
    : [];
  const downgradeRecovery = downgradeChildIds && downgradeChildren.length === 2
    ? downgradeRecoverySteps(downgradeChildren, computeStatus(data), data.plan.stagingVersion != null, jobs.length > 0)
    : null;
  const showDowngradeRecovery = !!downgradeRecovery && !downgradeRecovery.find((step) => step.key === 'author')?.done;
  const runDowngradeRecovery = async (key: DowngradeRecoveryStep['key']) => {
    if (!downgradeRecovery || !ensureClean(`running downgrade recovery ${key}`)) return;
    const step = downgradeRecovery.find((s) => s.key === key);
    if (!step || step.done || step.disabled) return;
    setBusy((b) => ({ ...b, [`downgrade-${key}`]: true }));
    try {
      if (key === 'direct') {
        await filmApi.directPlan(planId, { onlyMissing: true });
        await loadJobs();
        const outcome = await waitForPlanJob('FILM_DIRECT');
        if (outcome !== 'done') throw new Error(outcome === 'timeout' ? 'Dựng camera chưa xong; kiểm tra việc nền.' : 'Dựng camera thất bại; xem băng lỗi.');
      } else if (key === 'continuity') {
        await filmApi.checkContinuity(planId);
        await loadJobs();
        const outcome = await waitForPlanJob('FILM_CONTINUITY');
        if (outcome !== 'done') throw new Error(outcome === 'timeout' ? 'Continuity chưa xong; kiểm tra việc nền.' : 'Continuity thất bại; xem băng lỗi.');
      } else if (key === 'staging') {
        await filmApi.runScriptPasses(planId, false);
        await loadJobs();
        const outcome = await waitForPlanJob('FILM_SCRIPT_PASSES');
        if (outcome !== 'done') throw new Error(outcome === 'timeout' ? 'Dàn cảnh chưa xong; kiểm tra việc nền.' : 'Dàn cảnh thất bại; xem băng lỗi.');
      } else {
        for (const child of downgradeChildren) await filmApi.authorOneEventPrompt(child.id);
      }
      await reload();
      message.success('Đã xong bước đã chọn. Kiểm tra trạng thái rồi tự bấm bước kế tiếp.');
    } catch (e: any) {
      message.error(apiErrorText(e, 'Chuỗi gỡ sau khi hạ video'));
      await loadJobs();
    } finally { setBusy((b) => ({ ...b, [`downgrade-${key}`]: false })); }
  };
  const totalEvents = view.scenes.reduce((a, s) => a + s.events.length, 0);
  // Epic 2: event out-of-date sau khi sửa (server đã mark).
  const staleEvents = view.scenes.flatMap((s) => s.events).filter((e) => (e.staleStages || []).length > 0);
  const clipStaleN = staleEvents.filter((e) => (e.staleStages || []).includes('clip')).length;
  // Sync-stale = cần chạy pass rẻ (continuity + prompt) trước khi regen. Continuity riêng lẻ (event sau bị lệch chain) cũng tính.
  const syncStaleN = staleEvents.filter((e) => { const st = e.staleStages || []; return st.includes('prompt') || st.includes('continuity'); }).length;
  const hasSyncStale = syncStaleN > 0;

  return (
    <div className="sb-scope">
      {isDirty && (
        <div style={{ position: 'sticky', top: 0, zIndex: 20, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '6px 12px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text strong>● {nOps} unsaved change{nOps > 1 ? 's' : ''}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>— edit freely, then save once</Text>
          <span style={{ marginLeft: 'auto' }} />
          <Tooltip title="After saving, automatically re-author prompts for the edited events (uses AI). Turn off to sync manually with the Sync button."><Checkbox checked={autoSync} onChange={(e) => changeAutoSync(e.target.checked)} style={{ fontSize: 12 }}>Auto-sync</Checkbox></Tooltip>
          <Button size="small" onClick={undo} disabled={!history.length}>↶ Undo</Button>
          <Popconfirm title="Discard all unsaved changes?" onConfirm={discardDraft} okText="Discard" cancelText="Cancel" okButtonProps={{ danger: true }}>
            <Button size="small">Discard</Button>
          </Popconfirm>
          <Button size="small" type="primary" loading={busy.save} onClick={doSave}>💾 Save</Button>
        </div>
      )}
      {/*
        260810 — băng "chương bị nuốt dòng". Structure có lúc bỏ sót dòng nguồn (đo trên prod: cùng một
        chương 87 dòng, lượt phủ 100%, lượt 87.4% — mất 2 lượt thoại + 2 close-up). Máy vốn phát hiện được
        nhưng chỉ ghi logger.warn, mà prod để LOG_LEVEL='error' nên không ai thấy. Hiện thẳng ở đây, kèm
        đúng những dòng bị bỏ, để người bấm quyết: tạo lại plan hay thêm event bằng tay.
      */}
      {(() => {
        const cov = data.plan?.visualBible?.source_coverage;
        if (!cov || cov.ratio >= 1) return null;
        return (
          <div className="sb-stale-bar" style={{ background: '#fff1f0', borderColor: '#ffa39e' }}>
            <span>
              ⚠ Chương chưa được phủ hết: <b>{cov.covered}/{cov.total}</b> dòng ({(cov.ratio * 100).toFixed(1)}%) —{' '}
              <b>{cov.total - cov.covered}</b> dòng không event nào nhận.
            </span>
            <span style={{ marginLeft: 'auto' }} />
            <Tooltip
              title={<div style={{ maxWidth: 460 }}>
                {cov.swallowed.map((g) => <div key={g.line}>· dòng {g.line}: {g.text}</div>)}
                {cov.total - cov.covered > cov.swallowed.length && <div>… và {cov.total - cov.covered - cov.swallowed.length} dòng nữa</div>}
              </div>}
            >
              <Button size="small">Xem dòng bị bỏ</Button>
            </Tooltip>
          </div>
        );
      })()}
      {!isDirty && staleEvents.length > 0 && (
        <div className="sb-stale-bar">
          <span>⚠ {hasSyncStale
            ? `${syncStaleN} event${syncStaleN > 1 ? 's' : ''} need a re-sync after your edits — continuity & prompts (then regenerate the clips)`
            : `${clipStaleN} clip${clipStaleN > 1 ? 's' : ''} out of date — regenerate them to refresh`}.</span>
          <span style={{ marginLeft: 'auto' }} />
          {hasSyncStale && <Button size="small" loading={busy.sync} onClick={() => syncPrompts()}>Sync</Button>}
        </div>
      )}
      {/* E6-P1: việc nền đang chạy (server nói, không phải tab đoán) — sống qua reload, thấy cả việc do tab khác bấm. */}
      {jobs.length > 0 && (
        <div style={{ background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 12 }}>
          <Spin size="small" style={{ marginRight: 8 }} />
          <b>Đang chạy ở nền:</b>{' '}
          {jobs.map((j) => `${jobTitle(j.type, j.phase)}${j.eventId ? ' (1 event)' : ''} — ${j.runningMinutes}′`).join(' · ')}
          <span style={{ color: '#666' }}> — đừng bấm lại, mỗi lượt là một lần tính tiền.</span>
        </div>
      )}
      {/* Việc nền vừa CHẾT — trả 202 nên lý do không về được theo response; không hiện ở đây thì băng
          "đang chạy" chỉ biến mất và người dùng bấm lại mãi mà không biết vì sao (đo trên prod 260806). */}
      {groupedFailures.map((g) => (
        <Alert key={g.key} type="error" showIcon closable style={{ marginBottom: 8 }}
          onClose={() => setDismissedJobs(addDismissedJobs(g.ids))}
          message={<span>
            {jobTitle(g.type, g.phase)}{g.eventId ? ' (1 event)' : ''} thất bại{g.count > 1 ? ` ${g.count} lần` : ''}
            {' '}— gần nhất {g.minutesAgo}′ trước
          </span>}
          description={<span style={{ fontSize: 12 }}>{g.note}</span>}
        />
      ))}
      <PipelineStepper
        data={data}
        // Gộp job SERVER vào `busy`: nút phải mờ cả khi việc do tab khác (hoặc phiên trước) khởi động —
        // `busy` một mình chỉ biết những gì tab NÀY vừa bấm.
        busy={{
          ...busy,
          author: busy.author || jobRunning('FILM_AUTHOR_PROMPTS'),
          rebuildMismatch: busy.rebuildMismatch || jobRunning('FILM_AUTHOR_PROMPTS'),
          plates: busy.plates || jobRunning('FILM_GEN_PLATES'),
          outfits: busy.outfits || jobRunning('FILM_PREPARE_OUTFITS'),
          genall: busy.genall || jobRunning('FILM_GEN_EVENTS'),
        }}
        onRunPass={runPassByKey}
        onAuthor={authorPrompts} onGenAll={genAll} onVerify={runVerify} onRunRemaining={runRemaining} layoutFour={layoutFour}
        onOpenBank={() => { setBankTab(undefined); setBankOpen(true); }}
        onOpenSceneLooks={() => { setBankTab('scene-looks'); setBankOpen(true); }}
        sceneLooks={sceneLooks} genTuningByEvent={batchOverridesOf(tuningByEvent, data.scenes.flatMap((s) => s.events))} onOpenAssembly={() => setAsmOpen(true)}
        onOpenChat={() => setChatOpen(true)}
        onActiveStageChange={(st) => setCurrentStage(`${st.index + 1}. ${st.title}`)}
        onScrollEditor={() => editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        onBack={onBack} planName={planName} planStatus={planStatus} chapterIds={chapterIds}
        planPromptMode={data.plan.promptMode} onOpenChangePromptMode={openChangePromptModeModal}
        onRebuildMismatch={rebuildMismatchPrompts}
        onRebuildLookMismatch={rebuildLookMismatchPrompts}
      />
      <Modal
        title="Đổi kiểu prompt cả plan"
        open={!!modeModal}
        confirmLoading={modeModal?.loading}
        okButtonProps={{ disabled: modeModal?.loading }}
        onOk={confirmChangePromptMode}
        onCancel={() => setModeModal(null)}
        okText={modeModal ? `Đổi sang ${MODE_LABEL_VI[modeModal.target]}` : 'Đổi'}
        cancelText="Huỷ"
      >
        {modeModal?.loading && <Spin />}
        {modeModal?.preview && (
          <div style={{ fontSize: 13 }}>
            <p>Đổi plan sang mode <b>{MODE_LABEL_VI[modeModal.target]}</b>:</p>
            <ul>
              <li>{modeModal.preview.okAfter} event vẫn gen được (khớp mode hoặc đã ghim mode cũ)</li>
              {modeModal.preview.pinned.length > 0 && (
                <li>{modeModal.preview.pinned.length} event có prompt sửa tay <b>giữ mode cũ</b>: {formatBlockedOrders(modeModal.preview.pinned)}</li>
              )}
              {modeModal.preview.mismatchAfter.length > 0 && (
                <li style={{ color: '#cf1322' }}>{modeModal.preview.mismatchAfter.length} prompt AI viết sẽ <b>lệch mode</b> (chưa gen được tới khi dựng lại): {formatBlockedOrders(modeModal.preview.mismatchAfter)}</li>
              )}
              {modeModal.preview.missingAfter.length > 0 && (
                <li style={{ color: '#d46b08' }}>{modeModal.preview.missingAfter.length} event chưa có prompt: {formatBlockedOrders(modeModal.preview.missingAfter)}</li>
              )}
            </ul>
            {/* 260923 Epic2-P3 (U4) — N tính bằng CÙNG vị từ job dùng (shouldRebuildMismatchOf), không gán
                cứng mode đích cho event có đè riêng (N2 review). N=0 ⇒ ẩn ô. */}
            {(() => {
              const allEvents = data.scenes.flatMap((s) => s.events);
              const n = countRebuildableAfterModeSwitch(modeModal.preview!.mismatchAfter, allEvents, modeModal.target);
              if (n === 0) return null;
              return (
                <div style={{ marginTop: 8 }}>
                  <Checkbox checked={modeModal.rebuild} onChange={(e) => setModeModal((m) => (m ? { ...m, rebuild: e.target.checked } : m))}>
                    {/* 260925 (UT7) — câu cũ "theo mode cũ" đọc thành sẽ dựng lại THEO mode cũ (ngược nghĩa
                        thật: dựng lại theo mode MỚI, những prompt đang lệch mode). */}
                    Dựng lại ngay theo mode mới {n} prompt AI đang lệch mode ({n} lượt AI)
                  </Checkbox>
                  {/* M4 — Phase 1 "đổi mode không xoá chữ" không còn đúng khi ô này bật/nút Dựng lại được bấm. */}
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                    Chữ AI viết cũ sẽ bị GHI ĐÈ theo mode mới (không phải chỉ đổi nhãn) — đổi ngược lại sau đó tốn thêm AI lần nữa.
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </Modal>
      <Modal
        title="Hạ video 2.5 xuống hai video 2.0"
        open={!!downgradeModal}
        confirmLoading={downgradeModal?.accepting}
        okText="Duyệt cắt và chuyển cả hai sang 2.0"
        cancelText="Huỷ"
        okButtonProps={{ disabled: !downgradeModal?.preview?.result.ok || downgradeModal.selectedKeepBeatCount == null || downgradeModal.loading }}
        onOk={acceptDowngrade}
        onCancel={() => setDowngradeModal(null)}
        footer={downgradeModal?.errorCode === 'PREVIEW_STALE' || downgradeModal?.errorCode === 'CANDIDATE_GONE'
          ? <Space><Button onClick={() => setDowngradeModal(null)}>Đóng</Button><Button type="primary" onClick={reloadDowngradePreview}>Tải lại preview</Button></Space>
          : undefined}
      >
        {downgradeModal?.loading && <div style={{ textAlign: 'center', padding: 28 }}><Spin /><p>Đang kiểm tra nhịp, thoại và các giai đoạn… Chưa có thay đổi nào được ghi.</p></div>}
        {!downgradeModal?.loading && downgradeModal?.errorText && (
          <Alert type="warning" showIcon message={downgradeModal.errorText} description={downgradeModal.errorCode && `Mã: ${downgradeModal.errorCode}. Không có thay đổi nào được ghi.`} />
        )}
        {!downgradeModal?.loading && downgradeModal?.preview && (() => {
          const preview = downgradeModal.preview;
          if (!preview.result.ok) return <Alert type="warning" showIcon message={downgradeErrorText(preview.result.code)} description="Video vẫn giữ engine 2.5. Hãy dùng đường tách tay theo nhịp có sẵn." />;
          return <div style={{ fontSize: 13 }}>
            <p><Tag color="purple">E{preview.eventOrder} · ước 2.0 {preview.est20RawS.toFixed(1)}s</Tag> Chọn một ranh giới; hệ thống không chọn sẵn.</p>
            <Card size="small" title="Trước khi duyệt: những thứ sẽ mất" style={{ marginBottom: 12, background: '#fffbe6' }}>
              {downgradeLossLines(preview.losses).map((line) => <div key={line} style={{ marginBottom: 4 }}>• {line}</div>)}
            </Card>
            <Text strong>Chọn vị trí cắt · {preview.result.candidates.length} vị trí hợp lệ</Text>
            {preview.result.candidates.map((candidate, index) => {
              const selected = downgradeModal.selectedKeepBeatCount === candidate.keepBeatCount;
              return <label key={candidate.keepBeatCount} style={{ display: 'block', border: `1px solid ${selected ? '#1677ff' : candidate.tier === 'mid_flow' ? '#faad14' : '#d9d9d9'}`, background: selected ? '#f0f5ff' : '#fff', borderRadius: 6, padding: 10, marginTop: 8, cursor: 'pointer' }}>
                <input type="radio" name="sky-downgrade-cut" checked={selected} onChange={() => setDowngradeModal((m) => m ? { ...m, selectedKeepBeatCount: candidate.keepBeatCount } : m)} />{' '}
                <b>{index + 1}. {downgradeTierLabel(candidate.tier)} — sau giai đoạn {candidate.phaseOrder}</b>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 24px 1fr', gap: 6, alignItems: 'center', marginTop: 7 }}>
                  <div style={{ background: '#fafafa', padding: 6 }}><b>Video A · {candidate.first.durationS}s</b><br /><span style={{ color: '#666' }}>{candidate.first.beatCount} nhịp · {candidate.first.lineCount} câu</span>{candidate.first.lastLine?.textEn && <div>Thoại cuối: “{candidate.first.lastLine.textEn}”</div>}</div>
                  <div style={{ textAlign: 'center' }}>→</div>
                  <div style={{ background: '#fafafa', padding: 6 }}><b>Video B · {candidate.second.durationS}s</b><br /><span style={{ color: '#666' }}>{candidate.second.beatCount} nhịp · {candidate.second.lineCount} câu</span>{candidate.second.firstLine?.textEn && <div>Thoại đầu: “{candidate.second.firstLine.textEn}”</div>}<div><b>Mở đầu:</b> {candidate.openingStateB}</div></div>
                </div>
                <div style={{ marginTop: 5, color: candidate.tier === 'mid_flow' ? '#d46b08' : '#389e0d' }}>{candidate.tier === 'mid_flow' ? '⚠ Cắt giữa mạch; xem kỹ thoại cuối/đầu.' : '✓ Ranh giới được cắt.'}{candidate.cutAfterReason ? ` Lý do: ${candidate.cutAfterReason}` : ''}</div>
              </label>;
            })}
          </div>;
        })()}
      </Modal>
      {/* 260927 (mục B.6) — chữ khớp đường AVIS (ảnh người mặc), không nói "DC" như nghĩa cũ (gán mặt qua digital character). */}
      <Drawer title="Manage references — Nhân vật (ảnh người mặc) / Looks (outfits) / Location" width={1040}
        open={bankOpen} onClose={() => { setBankOpen(false); loadSceneLooks(); reload(); }} destroyOnClose>
        <FilmBankTab initialTab={bankTab} />
      </Drawer>
      <Drawer title="Assemble film → MP4 9:16 (audio + timed subtitles)" width={720}
        open={asmOpen} onClose={() => setAsmOpen(false)} destroyOnClose>
        <FilmExportTab eventReady={{ done: computeStatus(data).clipsDone, total: computeStatus(data).nEvents }} exportStale={data.plan.exportStale} />
      </Drawer>
      <Drawer title={<span><RobotOutlined style={{ color: '#A9762A', marginRight: 8 }} />AI assistant — propose edits into your draft</span>} width={480}
        open={chatOpen} onClose={() => setChatOpen(false)} styles={{ body: { display: 'flex', flexDirection: 'column' } }}>
        <FilmChatPanel layoutFour={layoutFour} planId={planId} view={view} onApplyOps={applyChatOps} onActionDone={reload}
          messages={chatMessages} onMessagesChange={setChatMessages} currentStage={currentStage} />
      </Drawer>
      {verify && (
        <Card size="small" style={{ marginBottom: 12 }} title={<Space>
          <Text strong>Grading result</Text>
          <Tag color={verify.judge.overall >= 4 ? 'green' : verify.judge.overall >= 3 ? 'orange' : 'red'}>overall {verify.judge.overall}/5</Tag>
          <Tag color={verify.lint.counts.error > 0 ? 'red' : 'green'}>{verify.lint.counts.error} hard errors</Tag>
          <Tag>{verify.lint.counts.warn} warnings</Tag>
        </Space>}>
          <Space wrap style={{ marginBottom: 6 }}>
            {Object.entries(verify.judge.scores).map(([k, v]) => (
              <Tag key={k} color={v >= 4 ? 'green' : v >= 3 ? 'orange' : 'red'}>{k}: {v}/5</Tag>
            ))}
          </Space>
          {Object.entries(verify.judge.flaws || {}).map(([k, arr]) => (arr && arr.length ? (
            <div key={k} style={{ fontSize: 11, marginBottom: 2 }}><Text strong>{k}:</Text> {arr.map((f, i) => <div key={i} style={{ color: '#cf1322', marginLeft: 8 }}>• {f}</div>)}</div>
          ) : null))}
          {verify.lint.findings.length > 0 && (
            <div style={{ fontSize: 11, marginTop: 6 }}>
              <Text strong>Linter:</Text>
              {verify.lint.findings.slice(0, 20).map((f, i) => (
                <div key={i} style={{ marginLeft: 8, color: f.severity === 'error' ? '#cf1322' : '#d48806' }}>[{f.severity}] {f.ref} — {f.message}</div>
              ))}
            </div>
          )}
        </Card>
      )}
      {/* 261010 P2a — liên kết sang khung mới 9 bước (Sơ đồ / Storyboard / Prompt có thao tác). Chỉ plan Sky; không đổi gì khác của UI cũ. */}
      {isSkyPlan(data.plan.flowVersion) && onOpenWorkflow && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Khung mới 9 bước cho plan dùng luồng mới — Sơ đồ, Storyboard, Prompt làm được ngay ở đó"
          action={<Button size="small" type="primary" onClick={onOpenWorkflow}>Mở khung mới</Button>} />
      )}
      {/* 261010 P2-1 — sơ đồ định vị: cấp PLAN, chỉ plan sky (plan khác gọi route sơ đồ ⇒ 409 NOT_SKY_PLAN nên KHÔNG mount ⇒ không gọi). */}
      {isSkyPlan(data.plan.flowVersion) && (
        <PositionMapPanel planId={planId} scenes={data.scenes} jobs={jobs} failures={failures} onJobsRefresh={loadJobs} />
      )}
      {totalEvents === 0 && <Empty description="Plan has no events yet — run Structure on the plan creation screen" style={{ margin: '20px 0' }} />}
      <div ref={editorRef} />
      <Collapse
        defaultActiveKey={view.scenes.map((s) => s.id)}
        items={view.scenes.map((sc) => ({
          key: sc.id,
          label: (
            <Space size={6} wrap>
              <Text strong style={{ fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: '.02em' }}>S{sc.orderIndex}. {sc.heading || sc.location || '(scene)'}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{sc.events.length} events</Text>
              <SceneTime scene={sc} onChange={setSceneTime} />
              <Tooltip title={sc.isLocked ? 'Locked — click to unlock' : 'Lock scene (skip on regen)'}>
                <Button type="text" size="small" icon={sc.isLocked ? <LockOutlined /> : <UnlockOutlined />} onClick={(e) => { e.stopPropagation(); toggleLock(sc.id, !sc.isLocked); }} />
              </Tooltip>
            </Space>
          ),
          children: (
            <>
              {(() => {
                // Banner storyboard: ảnh bối cảnh (plate) + mặt dàn nhân vật của cảnh → nhìn phát thấy "ở đâu · ai".
                // 260807: khớp rộng — xem chú thích ở `resolveLocationByName` (tên Bank ≠ tên scene.location).
                const plate = resolveLocationByName(sc.location, view.refBank?.locations) || undefined;
                const names = Array.from(new Set(sc.events.flatMap((e) => (e.shots || []).flatMap((s) => s.presence?.visible || []))));
                const speakers = Array.from(new Set(sc.events.flatMap((e) => (e.dialogueLines || []).map((d) => d.speakerName).filter(Boolean) as string[])));
                const who = names.length ? names : speakers;
                const faceOf = (n: string) => view.refBank?.characters.find((c) => castNameMatches(c, n));
                return (
                  <div className="sb-locbar">
                    <div className="sb-locthumb">
                      {plate?.plateUrl ? <img src={plate.plateUrl} alt="" /> : <span className="ph">🏙️</span>}
                    </div>
                    <div className="sb-locinfo">
                      <div className="sb-locname">📍 {sc.location || sc.heading || 'Location'}</div>
                      {sc.summary && <div className="sb-locsyn">{sc.summary}</div>}
                    </div>
                    <CastEditor names={who} options={(view.refBank?.characters || []).map((c) => c.name)} onChange={(v) => setSceneCast(sc.id, v)}>
                      <div className="sb-loccast">{who.length ? who.map((n) => <Face key={n} name={n} url={faceOf(n)?.previewUrl} />) : <span className="sb-hint">+ nhân vật</span>}</div>
                    </CastEditor>
                  </div>
                );
              })()}
              <div className="sb-events-grid">
                {sc.events.map((ev, i) => <EventCard key={ev.id} ev={ev} onSaveEvent={saveEvent} onSaveDialogue={saveDialogue} onSaveShot={saveShot} onDeleteShot={deleteShot} onSetEventCast={setEventCast} onMoveShot={moveShot} onAddShot={addShot} layoutFour={layoutFour} onGen={genEvent} onSelectClip={selectClip} busy={busy[`gen-${ev.id}`]} onSavePrompt={savePrompt} refBank={view.refBank} sceneLocation={sc.location} engine={view.plan.engine} flowVersion={view.plan.flowVersion} onDeleteClip={deleteClip}
                  onSplit={splitEvent} onMergeNext={mergeEventNext} onInsertAfter={insertEventAfter} onDeleteEvent={deleteEvent} canMergeNext={i < sc.events.length - 1}
                  dirty={!!(edit.ops.ev[ev.id] || edit.ops.prompt[ev.id] || edit.ops.dlg[ev.id])} dirtyShotIds={Object.keys(edit.ops.sh)}
                  generating={eventGenerating(ev.id)} tuning={tuningOf(ev.id)} onTuningChange={(t) => saveTuningFor(ev.id, t)}
                  onChangeEngine={changeEventVideoEngine} onOpenDowngrade={openDowngrade}
                  downgradeRecovery={downgradeChildIds?.includes(ev.id) && downgradeRecovery && showDowngradeRecovery ? { steps: downgradeRecovery, busy: Object.keys(busy).some((k) => k.startsWith('downgrade-') && busy[k]), onRun: runDowngradeRecovery } : undefined}
                  planPromptMode={view.plan.promptMode} onSetEventPromptMode={setEventPromptModeH}
                  onRebuildEventPrompt={rebuildEventPromptH} rebuildBusy={busy[`rebuild-${ev.id}`]} storyboard={storyboardCtx} />)}
              </div>
            </>
          ),
        }))}
      />
    </div>
  );
};

export default EventPlanTab;
